// Family-facing pages, rendered when URL hash starts with #familia
// Accessible via window.FamiliaRoutes.render({hash}) -> returns React node or null

const { useState: useStateF, useEffect: useEffectF } = React;

// Na impressão (botão PDF ou Ctrl+P) todos os kits abrem sozinhos:
// por lei a família pode comprar em outro lugar, então o PDF precisa listar
// cada item do kit pra ela levar na papelaria. flushSync garante que o React
// renderize aberto antes do navegador tirar o "snapshot" da página.
const famPrintSubs = new Set();
let famPrinting = false;
function setFamPrinting(v) {
  if (famPrinting === v) return;
  famPrinting = v;
  ReactDOM.flushSync(() => famPrintSubs.forEach(fn => fn(v)));
}
window.addEventListener("beforeprint", () => setFamPrinting(true));
window.addEventListener("afterprint", () => setFamPrinting(false));
if (window.matchMedia) {
  const mq = window.matchMedia("print");
  const onMq = e => setFamPrinting(e.matches);
  mq.addEventListener ? mq.addEventListener("change", onMq) : mq.addListener(onMq);
}
function useFamPrinting() {
  const [printing, setPrinting] = useStateF(famPrinting);
  useEffectF(() => {
    famPrintSubs.add(setPrinting);
    return () => { famPrintSubs.delete(setPrinting); };
  }, []);
  return printing;
}
// ga: de onde veio o "Baixar PDF" (série ou sacola), pro evento do GA da
// lista pública (src/analytics.js); no preview interno vai pra propriedade
// do wizard.
function printFamList(ga) {
  window.ScolistAnalytics.event("download_pdf", ga);
  setFamPrinting(true);
  window.print();
}

// BookCover, shows real cover from REAL_COVERS if available & loaded; falls back to
// an abstract cover using book.palette + title. Google Books sometimes returns a
// 128px placeholder "no image available", we detect that and treat as failure.
function BookCover({ book, size = "sm" }) {
  const REAL = window.REAL_COVERS || {};
  const LOCAL = window.LOCAL_COVERS || {};
  const ISBN = window.ISBN_COVERS || {};
  const isbn = (book && book.isbn ? String(book.isbn) : "").trim();
  const catalogUrl = book && book.coverFromCatalog ? book.imageUrl : null;
  const realUrl = catalogUrl || (isbn && ISBN[isbn]) || LOCAL[book.id] || REAL[book.id] || (book && book.imageUrl) || null;
  const [failed, setFailed] = React.useState(false);
  const dims = size === "sm" ? { w: 60, h: 85, ft: 8.5, pad: 6 }
            : size === "md" ? { w: 80, h: 115, ft: 10, pad: 8 }
            : size === "lg" ? { w: 260, h: 380, ft: 20, pad: 18 }
            : { w: 110, h: 160, ft: 12, pad: 10 };
  const palette = book.palette || ["#2c4ffc", "#c7de40", "#fff"];

  if (realUrl && !failed) {
    return (
      <img
        src={realUrl}
        alt={book.title}
        onLoad={(e) => {
          // Google Books returns a 128×164 "no cover" placeholder, detect & fallback
          if (e.target.naturalWidth <= 128 && e.target.naturalHeight <= 200) setFailed(true);
        }}
        onError={() => setFailed(true)}
        style={{
          width: dims.w, height: dims.h,
          objectFit: "cover",
          borderRadius: 4,
          boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
          flexShrink: 0,
          background: "#f0f0f0",
          display: "block",
        }}
      />
    );
  }

  // Abstract fallback, palette-based
  const [c0, c1, c2] = palette;
  const shortTitle = (book.title || "").length > 32 ? (book.title || "").slice(0, 30) + "…" : (book.title || "");
  return (
    <div style={{
      width: dims.w, height: dims.h,
      borderRadius: 4,
      background: `linear-gradient(135deg, ${c0}, ${c1 || c0})`,
      boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      padding: dims.pad,
      overflow: "hidden",
      position: "relative",
    }}>
      <div style={{
        fontSize: dims.ft,
        fontWeight: 700,
        color: c2 || "#fff",
        lineHeight: 1.15,
        textShadow: "0 1px 2px rgba(0,0,0,0.2)",
      }}>{shortTitle}</div>
    </div>
  );
}
window.BookCover = BookCover;

function slugifyF(s) {
  return (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function moneyF(n) { return "R$ " + (Number(n) || 0).toFixed(2).replace(".", ","); }

// Pull the live state from the app (data + CATALOG are injected)
//
// Só retorna o que a escola realmente pinou — sem completar com sugestões
// do catálogo pra bater a cota PT/EN da calibragem (qtyPerYearPT/EN). Essa
// função chegou a fazer esse preenchimento automático (extraPT/extraEN),
// igual ao que o Step11_Confirm também fazia até ser removido de lá
// (steps-final.jsx, commit 7fa321d7, 27/08/2026 — "a lista deve ser uma
// só", reportado pelo Alex: uma série cheia de sugestões enquanto outra,
// com curadoria de verdade, tinha só 2). Aqui ficou de fora daquele fix e
// seguiu preenchendo a lista da família e o link público com livros que a
// escola nunca escolheu (bug reportado pelo Alex de novo, 11/09/2026: "só
// adicionei 2 livros e a lista pública está com vários" — série sem
// nenhum livro pinado aparecia com 3-5 livros do catálogo estático, sem
// nenhuma marca visual diferenciando do que foi escolhido de verdade).
function resolveBooksForSeries(data, seriesIdx) {
  const CATALOG = window.CATALOG || [];
  const pinned = data.pinned || {};
  const pinnedIds = pinned[seriesIdx] || [];
  const pinnedBooks = pinnedIds.map(id => CATALOG.find(b => b.id === id)).filter(Boolean);

  // Agrupa por idioma (PT, EN, ES, FR, DE, depois outros — ordem de
  // LIT_LANG_META) e, dentro de cada idioma, prioriza livros com capa real
  // (REAL_COVERS) na frente. Antes só ordenava por capa e os idiomas saíam
  // misturados na lista pública.
  const REAL = window.REAL_COVERS || {};
  const LOCAL = window.LOCAL_COVERS || {};
  const hasCover = (id) => !!(LOCAL[id] || REAL[id]);
  const langOrder = Object.keys(window.LIT_LANG_META || { pt: 1, en: 1 });
  const langRank = (b) => {
    const i = langOrder.indexOf(b.lang || "pt");
    return i === -1 ? langOrder.length : i;
  };
  return [...pinnedBooks].sort((a, b) =>
    (langRank(a) - langRank(b)) ||
    String(a.lang || "pt").localeCompare(String(b.lang || "pt")) ||
    (hasCover(b.id) - hasCover(a.id)));
}

// ============= DESIGN SYSTEM (Scolex) =============
// Tokens do design system Scolex usados pela lista da família (layout novo,
// "Lista Família.html" do protótipo). Ficam escopados em .fam-ds em vez de
// :root porque o wizard já define variáveis com o mesmo nome
// (--radius-card, --shadow-sm, --teal…) com outros valores no index.html.
// Temporada da lista (Passo 1) — ver seasonOf em shared.jsx.
const famSeason = (data) => (window.seasonOf ? window.seasonOf(data) : "2026");
const famSeasonFile = (data) => famSeason(data).replace(/\//g, "-");
const FAM_DS_CSS = `
.fam-ds{
  --teal-900:#12333A;--teal-800:#164E5B;--teal-700:#1C4E58;--teal-600:#1C5555;--teal-500:#22616C;
  --teal-400:#2C8F9A;--teal-300:#2E96A1;--teal-200:#7FC4CC;--teal-100:#BFE0E4;--teal-050:#ECF7F8;
  --green-700:#3F8722;--green-600:#4FA32A;--green-400:#97C93D;--green-100:#D8E8D6;--green-050:#EDF4E4;
  --sun-600:#B5841C;--sun-400:#F7CC1B;--sun-100:#FDEBC0;--sun-050:#FEF4DD;
  --paper-050:#F7F5EF;--paper-100:#F5FAF9;--paper-200:#EAF4F3;
  --slate-600:#5E7378;--slate-400:#9DB0B2;--slate-200:#D6E2E3;
  --text-strong:var(--teal-700);--text-body:#235C66;--text-muted:var(--slate-600);
  --text-on-dark-muted:#BFE0E4;
  --surface-page:#FFFFFF;--surface-sunken:var(--teal-050);--surface-card:#FFFFFF;
  --surface-band-green:var(--green-100);--surface-band-cream:var(--paper-050);
  --border-card:#E4EEEE;
  --action-bg:var(--sun-400);--action-fg:var(--teal-700);--action-bg-hover:#FFD84A;
  --hero-gradient:linear-gradient(105deg,#1C4E58 0%,#22616C 38%,#287A85 68%,#398F85 100%);
  --shadow-card:0 2px 10px rgba(18,51,58,.06),0 12px 28px rgba(18,51,58,.05);
  --shadow-card-hover:0 4px 14px rgba(18,51,58,.09),0 18px 40px rgba(18,51,58,.08);
  --shadow-cover:0 4px 12px rgba(18,51,58,.18);
  --shadow-action:0 6px 16px rgba(247,204,27,.35);
  --radius-sm:10px;--radius-card:18px;--radius-pill:999px;--radius-cover:4px;
  --ls-eyebrow:0.18em;--ls-badge:0.06em;
  --font-core:'Fredoka','Nunito','Segoe UI',system-ui,sans-serif;
  --safe-b:env(safe-area-inset-bottom,0px);
  min-height:100vh;background:var(--surface-page);color:var(--text-body);
  font:400 17px/1.55 var(--font-core);-webkit-font-smoothing:antialiased;
}
.fam-ds button{font:inherit;color:inherit;cursor:pointer}
.fam-ds h1,.fam-ds h2{font-family:var(--font-core)}
@keyframes famRiseIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
@keyframes famPopIn{from{opacity:0;transform:scale(.96)}to{opacity:1;transform:none}}
@keyframes famSpin{to{transform:rotate(360deg)}}
@keyframes famBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
@media (prefers-reduced-motion:reduce){.fam-bob{animation:none!important}}
.fam-print-only{display:none}
.fam-sac-rows{max-height:38vh;overflow-y:auto;overscroll-behavior:contain}
.fam-sac-row{display:grid;grid-template-columns:40px minmax(0,1fr) auto 22px;grid-template-areas:"av main enc x";align-items:center;column-gap:14px;row-gap:8px;padding:13px 0;border-top:1px solid var(--border-card)}
.fam-sac-av{grid-area:av;width:40px;height:40px;border-radius:999px;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:700;color:var(--teal-700)}
.fam-sac-main{grid-area:main;min-width:0}
.fam-sac-enc{grid-area:enc;display:flex;align-items:center;gap:8px;background:none;border:none;padding:0;font-size:14px;color:var(--text-body)}
.fam-sac-x{grid-area:x;background:none;border:none;padding:0;font-size:22px;line-height:1;color:var(--slate-400,#8a9a9d)}
.fam-sac-series{position:relative;display:inline-block;white-space:nowrap}
.fam-sac-series select{position:absolute;inset:0;width:100%;opacity:0;cursor:pointer;font-size:16px}
.fam-sac-edit{display:none;grid-area:edit;justify-self:end}
.fam-sac-foot{display:flex;align-items:center;gap:14px;flex-wrap:wrap;padding-top:14px;border-top:1px solid var(--border-card)}
.fam-sac-foot-btns{display:flex;gap:10px;margin-left:auto}
@media (max-width:520px){
  .fam-sac-row{grid-template-columns:40px minmax(0,1fr) auto 22px;grid-template-areas:"av main main x" "av enc edit edit";column-gap:12px}
  .fam-sac-edit{display:block}
  .fam-sac-edit-inline{display:none}
  .fam-sac-av{align-self:start}
  .fam-sac-enc{justify-self:start}
  .fam-sac-rows{max-height:30vh}
  .fam-sac-foot{gap:10px;padding-top:12px}
  .fam-sac-foot-btns{width:100%}
  .fam-sac-foot-btns>*{flex:1}
}
@media print{
  @page{margin:12mm 12mm 14mm}
  .no-print{display:none!important}
  .fam-print-only{display:block!important}
  .fam-ds{background:#fff;padding-bottom:0!important}
  .fam-ds *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .fam-item,.fam-print-row{break-inside:avoid;page-break-inside:avoid}
  .fam-print-head{break-after:avoid;page-break-after:avoid}
  .fam-ds *{animation:none!important}
}
`;

// ============= SHARED FRAME =============
function FamilyFrame({ data, children, onBack, flush }) {
  // Mesma prioridade do LandingBrandingCard (steps-final.jsx): logo/accent
  // persistidos em account.school primeiro, data.school* como fallback.
  // Antes essa página só lia data.schoolLogoUrl, que só existe na sessão em
  // que o upload aconteceu — reabrindo o link (ou logando de novo) sem
  // re-upar a logo, ela sumia mesmo já salva no backend da escola (bug
  // reportado pela Joana, reunião 26/08/2026: "a logo já tá aqui, não sei
  // por que não apareceu lá").
  const { account } = useApp();
  const accent = (account && account.school && account.school.accent) || data.schoolAccent || "#2E96A1";
  const logoUrl = (account && account.school && account.school.logoUrl) || data.schoolLogoUrl;
  const schoolName = data.schoolName || "Sua escola";
  const initials = schoolName.split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <div className="fam-ds">
      <style>{FAM_DS_CSS}</style>
      <div style={{
        display: "flex", alignItems: "center", gap: 11,
        padding: "12px 16px", background: "#fff",
        borderBottom: flush ? "none" : "1px solid var(--border-card)",
      }}>
        {onBack && (
          <button className="no-print" onClick={onBack} aria-label="Voltar para todas as séries" style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: "#fff",
            border: "1px solid var(--border-card)", color: "var(--text-body)", fontSize: 15,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>←</button>
        )}
        {logoUrl
          ? <img src={logoUrl} alt={schoolName} style={{ height: 34, maxWidth: 110, objectFit: "contain" }}/>
          : <span style={{
              width: 34, height: 34, borderRadius: 9, background: accent, color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 700, flexShrink: 0,
            }}>{initials}</span>}
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 13.5, fontWeight: 700, color: "var(--text-strong)", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {schoolName}
          </span>
          <span style={{ display: "block", fontSize: 11, color: "var(--text-muted)" }}>Lista de volta às aulas {famSeason(data)}</span>
        </span>
        <span style={{ marginLeft: "auto", flexShrink: 0 }}>
          <img src="assets/scolados-logo.png" alt="Scolados" style={{ height: 19 }}/>
        </span>
      </div>

      {children}
    </div>
  );
}

// Export Excel with 3 sheets: Literatura · Didáticos · Papelaria — for coordinators
function exportCoordinatorCSV(data) {
  // Séries de verdade da escola (Passo 1), não a lista fixa de 12.
  const activeSeries = window.resolveSeriesList ? window.resolveSeriesList(data) : (window.SERIES_LIST || []);
  const PAPELARIA_CAT = window.PAPELARIA_CATALOG || [];
  const PAPELARIA_PARTNERS = window.PAPELARIA_PARTNERS || {};
  const fname = `lista-completa-${slugifyF(data.schoolName || "escola")}-${famSeasonFile(data)}`;

  // Fallback: se XLSX não tiver carregado, exporta CSV só de literatura como antes
  if (!window.XLSX) {
    const rows = [["Série", "Título", "Autor", "Editora", "Idioma", "Nível", "Páginas", "Preço"]];
    activeSeries.forEach((s, i) => {
      const bks = resolveBooksForSeries(data, i);
      bks.forEach(b => {
        rows.push([s, b.title || "", b.author || "", b.editora || "",
          b.lang === "pt" ? "Português" : "Inglês",
          b.level || "", b.pages || "",
          (b.price != null ? Number(b.price).toFixed(2) : "")]);
      });
    });
    const csv = rows.map(r => r.map(c => {
      const s = String(c).replace(/"/g, '""');
      return /[",;\n]/.test(s) ? `"${s}"` : s;
    }).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${fname}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
    return;
  }

  const XLSX = window.XLSX;
  const wb = XLSX.utils.book_new();

  // --- Aba 1: Literatura ---
  const { label: periodLabel, abbrev: periodAbbrev } = window.periodInfo ? window.periodInfo(data) : { label: "bimestre", abbrev: "bim" };
  const periodHeader = periodLabel.charAt(0).toUpperCase() + periodLabel.slice(1);
  const litRows = [["Série", "Título", "Autor", "Editora", "Idioma", "Nível", "Páginas", periodHeader, "Preço"]];
  const bimestres = data.bimestres || {};
  activeSeries.forEach((s, i) => {
    const bks = resolveBooksForSeries(data, i);
    const seriesBim = bimestres[i] || {};
    bks.forEach(b => {
      const bn = seriesBim[b.id] ? `${seriesBim[b.id]}º ${periodAbbrev}` : "";
      litRows.push([
        s, b.title || "", b.author || "", b.editora || "",
        b.lang === "pt" ? "Português" : (b.lang === "en" ? "Inglês" : (b.lang || "")),
        b.level || "", b.pages || "", bn,
        (b.price != null ? Number(b.price) : ""),
      ]);
    });
  });
  if (litRows.length === 1) litRows.push(["—", "(sem livros selecionados)", "", "", "", "", "", "", ""]);
  const wsLit = XLSX.utils.aoa_to_sheet(litRows);
  for (let R = 2; R <= litRows.length; R++) {
    const c = wsLit["I" + R]; if (c && typeof c.v === "number") c.z = '"R$" #,##0.00';
  }
  wsLit["!cols"] = [{wch:14},{wch:38},{wch:22},{wch:18},{wch:11},{wch:8},{wch:9},{wch:11},{wch:12}];
  XLSX.utils.book_append_sheet(wb, wsLit, "Literatura");

  // --- Aba 2: Didáticos ---
  const didRows = [["Série", "Título", "Editora", "Disciplina", "Idioma", "Formato", "Tipo", "Preço"]];
  const didaticosAdopted = data.didaticosAdopted || {};
  activeSeries.forEach((s, i) => {
    // priceBySeries no catálogo estático usa o nome CANÔNICO da série, não o
    // rótulo customizado pela escola (ver inferCanonicalSeriesName, shared.jsx).
    const canonicalName = window.inferCanonicalSeriesName ? window.inferCanonicalSeriesName(s) : s;
    const list = didaticosAdopted[i] || [];
    list.forEach(a => {
      // resolveDidatico (steps-didaticos.jsx) também cobre didáticos da busca
      // ao vivo — o catálogo estático sozinho deixava esses itens de fora.
      const it = resolveDidatico(a);
      if (!it) return;
      const price = didaticoPrice(it, s, canonicalName);
      didRows.push([
        s, it.title || "", it.publisher || it.editora || "",
        it.subject || "", (it.lang || "").toUpperCase(),
        it.format || "", it.kind || "",
        price || "",
      ]);
    });
  });
  if (didRows.length === 1) didRows.push(["—", "(nenhum didático adotado)", "", "", "", "", "", ""]);
  const wsDid = XLSX.utils.aoa_to_sheet(didRows);
  for (let R = 2; R <= didRows.length; R++) {
    const c = wsDid["H" + R]; if (c && typeof c.v === "number") c.z = '"R$" #,##0.00';
  }
  wsDid["!cols"] = [{wch:14},{wch:36},{wch:22},{wch:14},{wch:7},{wch:11},{wch:10},{wch:12}];
  XLSX.utils.book_append_sheet(wb, wsDid, "Didáticos");

  // --- Aba 3: Papelaria ---
  const papRows = [["Série", "Item", "Marca/Parceiro", "Quantidade", "Preço unitário", "Total"]];
  const papelariaCart = data.papelaria || {};
  activeSeries.forEach((s, i) => {
    const list = papelariaCart[i] || [];
    list.forEach(c => {
      const it = PAPELARIA_CAT.find(x => x.id === c.itemId);
      if (!it) return;
      const br = it.brands?.[c.brandIdx];
      const partner = br ? PAPELARIA_PARTNERS[br.partnerId] : null;
      const unit = br ? br.price : 0;
      const qty = c.qty || 1;
      papRows.push([
        s, it.genericName || it.name || "",
        br?.partnerId === "vtex" ? "" : (partner?.name || br?.partnerId || ""),
        qty, unit, unit * qty,
      ]);
    });
    // Materiais customizados (escola própria)
    (data.customMaterials || []).forEach(m => {
      if (m.seriesApplied && !m.seriesApplied.includes(i)) return;
      papRows.push([s, m.name || "(material da escola)", m.brand || "—", m.qty || 1, m.price || "", (m.price || 0) * (m.qty || 1)]);
    });
  });
  if (papRows.length === 1) papRows.push(["—", "(nenhum item de papelaria)", "", "", "", ""]);
  const wsPap = XLSX.utils.aoa_to_sheet(papRows);
  for (let R = 2; R <= papRows.length; R++) {
    ["E", "F"].forEach(col => {
      const cell = wsPap[col + R]; if (cell && typeof cell.v === "number") cell.z = '"R$" #,##0.00';
    });
  }
  wsPap["!cols"] = [{wch:14},{wch:36},{wch:22},{wch:11},{wch:14},{wch:14}];
  XLSX.utils.book_append_sheet(wb, wsPap, "Papelaria");

  // --- Aba 4: Complementos (só quando a escola usou o passo) ---
  const resolveCmp = window.resolveComplementosForSeries;
  const cmpRows = [["Série", "Item", "Marca", "Quantidade", "Preço unitário", "Total"]];
  activeSeries.forEach((s, i) => {
    (resolveCmp ? resolveCmp(data, i) : []).forEach(c => {
      cmpRows.push([s, c.name, c.brand || "", c.qty, c.price != null ? c.price : "", c.price != null ? c.price * c.qty : ""]);
    });
  });
  if (cmpRows.length > 1) {
    const wsCmp = XLSX.utils.aoa_to_sheet(cmpRows);
    for (let R = 2; R <= cmpRows.length; R++) {
      ["E", "F"].forEach(col => {
        const cell = wsCmp[col + R]; if (cell && typeof cell.v === "number") cell.z = '"R$" #,##0.00';
      });
    }
    wsCmp["!cols"] = [{wch:14},{wch:40},{wch:18},{wch:11},{wch:14},{wch:14}];
    XLSX.utils.book_append_sheet(wb, wsCmp, "Complementos");
  }

  XLSX.writeFile(wb, `${fname}.xlsx`);
}

// Exportação alternativa pedida pela Joana (item 20,
// TAREFAS-JOANA-2026-08-21.md): uma aba por turma (em vez de uma aba por
// categoria), colunas simples pra mandar direto pra compra/conferência.
// Papelaria e outros materiais entram com ISBN vazio (não têm).
function exportListaPorTurma(data) {
  // Séries de verdade da escola (Passo 1), não a lista fixa de 12.
  const activeSeries = window.resolveSeriesList ? window.resolveSeriesList(data) : (window.SERIES_LIST || []);
  const PAPELARIA_CAT = window.PAPELARIA_CATALOG || [];
  const PAPELARIA_PARTNERS = window.PAPELARIA_PARTNERS || {};
  const fname = `lista-por-turma-${slugifyF(data.schoolName || "escola")}-${famSeasonFile(data)}`;
  // Preço é o total da linha (unitário × Qtde). Kit só vale pra papelaria:
  // o marcado no Passo de papelaria ou, sem marcação, o da categoria — o
  // mesmo critério da lista da família (FAM_MOCHILA_CATS).
  const header = ["Série", "Categoria", "Kit", "ISBN", "Título", "Qtde", "Preço", "Opcional"];
  const KIT_LABEL = { mochila: "Kit mochila", escola: "Kit escola" };

  const rowsForSeries = (s, i) => {
    const rows = [];
    // Só livros de literatura podem ser marcados opcionais (Passo 13,
    // Confirmação final) — didáticos, papelaria e outros materiais ficam
    // sempre em branco nessa coluna.
    const optionalIds = new Set((data.optionalBooks || {})[i] || []);

    resolveBooksForSeries(data, i).forEach((b) => {
      rows.push([s, "Literatura", "", b.isbn || "", b.title || "", 1, b.price != null ? Number(b.price) : "", optionalIds.has(b.id) ? "Sim" : ""]);
    });

    ((data.didaticosAdopted || {})[i] || []).forEach((a) => {
      const it = resolveDidatico(a);
      if (!it) return;
      // priceBySeries no catálogo estático usa o nome CANÔNICO da série, não
      // o rótulo customizado (ver inferCanonicalSeriesName, shared.jsx).
      const canonicalName = window.inferCanonicalSeriesName ? window.inferCanonicalSeriesName(s) : s;
      const price = didaticoPrice(it, s, canonicalName);
      rows.push([s, "Didáticos", "", it.isbn || "", it.title || "", 1, price || "", ""]);
    });

    ((data.papelaria || {})[i] || []).forEach((c) => {
      const it = PAPELARIA_CAT.find((x) => x.id === c.itemId);
      if (!it) return;
      const br = it.brands?.[c.brandIdx];
      const unit = br ? br.price : 0;
      const qty = c.qty || 1;
      const partner = br ? PAPELARIA_PARTNERS[br.partnerId] : null;
      const label = partner && br.partnerId !== "vtex" ? `${it.genericName} (${partner.name})` : (it.genericName || it.name || "");
      const kit = c.kit || (FAM_MOCHILA_CATS.includes(it.category) ? "mochila" : "escola");
      rows.push([s, "Papelaria", KIT_LABEL[kit] || kit, "", label, qty, unit * qty, ""]);
    });

    (window.resolveComplementosForSeries ? window.resolveComplementosForSeries(data, i) : []).forEach((c) => {
      rows.push([s, "Complementos", "", c.isBook ? (c.ean || "") : "", c.name, c.qty, c.price != null ? c.price * c.qty : "", ""]);
    });

    (data.customMaterials || []).forEach((m) => {
      if (m.seriesApplied && !m.seriesApplied.includes(i)) return;
      rows.push([s, "Outros", "", "", m.name || "(material da escola)", m.qty || 1, (m.price || 0) * (m.qty || 1), ""]);
    });

    return rows;
  };

  // Fallback: se XLSX não tiver carregado, exporta tudo num CSV só (sem abas)
  if (!window.XLSX) {
    const rows = [header];
    activeSeries.forEach((s, i) => rows.push(...rowsForSeries(s, i)));
    const csv = rows.map(r => r.map(c => {
      const str = String(c).replace(/"/g, '""');
      return /[",;\n]/.test(str) ? `"${str}"` : str;
    }).join(";")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${fname}.csv`;
    document.body.appendChild(a); a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 500);
    return;
  }

  const XLSX = window.XLSX;
  const wb = XLSX.utils.book_new();

  // Nomes de aba do Excel são limitados a 31 caracteres, não podem conter
  // \ / ? * [ ] : , não podem ficar vazios, nem se repetir — qualquer um
  // desses casos faz o Excel considerar o arquivo corrompido ao abrir.
  // Séries têm nome livre (renomeável desde o ajuste de segmento manual),
  // então nada disso é garantido só pelo nome da série.
  const usedSheetNames = new Set();
  const safeSheetName = (raw, fallbackIdx) => {
    let name = String(raw || "").replace(/[/\\?*[\]:]/g, "").trim().slice(0, 31) || `Turma ${fallbackIdx + 1}`;
    let candidate = name;
    let suffix = 2;
    while (usedSheetNames.has(candidate)) {
      candidate = `${name.slice(0, 28)} (${suffix})`;
      suffix++;
    }
    usedSheetNames.add(candidate);
    return candidate;
  };

  const appendSheet = (rows, name) => {
    if (rows.length === 1) rows.push(["—", "(sem itens)", "", "", "", "", "", ""]);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    for (let R = 2; R <= rows.length; R++) {
      const c = ws["G" + R]; if (c && typeof c.v === "number") c.z = '"R$" #,##0.00';
    }
    ws["!cols"] = [{ wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 42 }, { wch: 6 }, { wch: 12 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws, name);
  };

  // Primeira aba com todas as séries juntas: Excel/Numbers e as prévias de
  // celular/e-mail abrem só a primeira aba, e com uma aba por turma parecia
  // que o arquivo só tinha a primeira série (reportado pelo Alex, 13/10/2026:
  // "só está vindo itens do Infantil 2").
  const perSeries = activeSeries.map((s, i) => rowsForSeries(s, i));
  appendSheet([header, ...perSeries.flat()], safeSheetName("Todas as séries", 0));
  activeSeries.forEach((s, i) => appendSheet([header, ...perSeries[i]], safeSheetName(s, i)));

  XLSX.writeFile(wb, `${fname}.xlsx`);
}

// ============= DADOS DE UMA SÉRIE =============
// Literatura (só o que a escola pinou), didáticos adotados e papelaria
// agrupada nos dois kits (mochila/escola) — o mesmo recorte que o
// SerieFamilia antigo mostrava, só que num formato único de item pro layout.
const FAM_MOCHILA_CATS = ["Cadernos", "Escrita", "Apagar", "Medição", "Estojo/Mochila", "Pastas"];
const FAM_KITS = [
  { id: "mochila", label: "Kit mochila" },
  { id: "escola", label: "Kit escola" },
];

function famSeriesItems(data, seriesIdx, seriesName) {
  // priceBySeries no catálogo estático usa o nome CANÔNICO da série, não o
  // rótulo customizado pela escola (ver inferCanonicalSeriesName, shared.jsx).
  const canonicalSeriesName = window.inferCanonicalSeriesName ? window.inferCanonicalSeriesName(seriesName) : seriesName;
  const optionalIds = new Set(((data.optionalBooks || {})[seriesIdx]) || []);
  const bims = (data.bimestres || {})[seriesIdx] || {};
  const LANG = window.LIT_LANG_META || {};

  const litItems = resolveBooksForSeries(data, seriesIdx).map(b => ({
    key: "lit:" + b.id,
    kind: "lit",
    book: b,
    title: b.title,
    sub: b.author,
    // Nível Scolex é classificação interna: não aparece pra família (card nem PDF).
    meta: [b.editora, b.pages ? `${b.pages} páginas` : null].filter(Boolean),
    isbn: b.isbn || null,
    lang: b.lang && b.lang !== "pt" ? ((LANG[b.lang] && LANG[b.lang].label) || b.lang.toUpperCase()) : null,
    bimestre: bims[b.id] || null,
    summary: b.summary || null,
    optional: optionalIds.has(b.id),
    bookId: b.id,
    price: b.price != null ? Number(b.price) : null,
  }));

  // resolveDidatico (steps-didaticos.jsx) cobre também os didáticos da busca
  // ao vivo — window.DIDATICOS (catálogo estático) sozinho deixava esses
  // itens fora da lista da família.
  const didItems = (((data.didaticosAdopted || {})[seriesIdx]) || []).map(a => {
    const d = resolveDidatico(a);
    if (!d) return null;
    return {
      key: "did:" + d.id,
      kind: "did",
      title: d.title,
      sub: d.publisher || d.editora,
      meta: [
        d.subject ? subjectLabel(d.subject) : null,
        d.format === "hibrido" ? "Físico + digital" : d.format === "digital" ? "Digital" : d.format ? "Físico" : null,
      ].filter(Boolean),
      isbn: d.isbn || null,
      coverUrl: d.coverImageUrl || d.imageUrl || null,
      optional: false,
      didaticoId: d.id,
      price: didaticoPrice(d, seriesName, canonicalSeriesName) || null,
    };
  }).filter(Boolean);

  // Papelaria: cada item vai pro kit marcado no Passo de papelaria (c.kit);
  // sem marcação, cai no kit pela categoria do catálogo.
  const PAPELARIA = window.PAPELARIA_CATALOG || [];
  const PARTNERS = window.PAPELARIA_PARTNERS || {};
  const papParts = (((data.papelaria || {})[seriesIdx]) || []).map(c => {
    const it = PAPELARIA.find(x => x.id === c.itemId);
    if (!it) return null;
    const br = it.brands?.[c.brandIdx];
    if (!br) return null;
    const partner = PARTNERS[br.partnerId];
    // Item da VTEX: genericName já é o nome completo do produto (com o SKU)
    // e model repetia o mesmo nome — mostra só o nome, sem "Loja parceira".
    const isVtex = br.partnerId === "vtex" || it.provider === "vtex";
    const model = br.brand || br.model;
    return {
      id: it.id,
      title: it.genericName,
      sub: isVtex ? null : [model && model !== it.genericName ? model : null, partner?.name || br.partnerId].filter(Boolean).join(" · ") || null,
      qty: c.qty || 1,
      kit: c.kit || (FAM_MOCHILA_CATS.includes(it.category) ? "mochila" : "escola"),
      brandIdx: c.brandIdx,
      partnerId: br.partnerId || null,
      model: br.model || br.brand || null,
      unit: it.unit || null,
      price: br.price != null ? Number(br.price) : null,
      // Itens buscados na VTEX (Passo de papelaria) têm id "vtex-<productId>"
      // — ver mapStationeryResultToItem em api.js.
      vtexProductId: (it.provider === "vtex" || br.partnerId === "vtex") && /^vtex-\d+$/.test(it.id)
        ? it.id.slice(5) : null,
    };
  }).filter(Boolean);
  // Kit cadastrado na VTEX pra série (data.papelariaKitsBySeries, só no link
  // público): o card vai pro carrinho como esse SKU, no lugar dos itens
  // soltos. Kit sem nenhum item da escola não aparece, nem com kit na VTEX
  // (2026-10-27; antes o kit da VTEX sempre valia, decisão de 2026-10-21).
  const seriesKits = ((data.papelariaKitsBySeries || [])[seriesIdx]) || {};
  const papItems = FAM_KITS.map(k => {
    const parts = papParts.filter(p => p.kit === k.id);
    const vtexKit = seriesKits[k.id] || null;
    if (parts.length === 0) return null;
    return {
      key: "pap:" + k.id,
      kind: "pap",
      title: `${k.label} · ${seriesName}`,
      meta: parts.length ? [`${parts.length} ${parts.length === 1 ? "item" : "itens"}`] : [],
      contents: parts,
      optional: false,
      kitSkuId: vtexKit ? vtexKit.skuId : null,
      kitName: vtexKit ? vtexKit.name : null,
    };
  }).filter(Boolean);

  // Complementos (Passo Itens complementares, steps-complementos.jsx): um
  // card por item, cada um com seu checkbox. Lista sem o campo = [].
  const resolveCmp = window.resolveComplementosForSeries;
  const cmpItems = (resolveCmp ? resolveCmp(data, seriesIdx) : []).map(c => ({
    key: "cmp:" + c.itemId,
    kind: "cmp",
    title: c.name,
    sub: [c.brand, c.model && c.model !== c.name ? c.model : null].filter(Boolean).join(" · ") || null,
    meta: [`${c.qty} ${c.qty === 1 ? "unidade" : "unidades"}`],
    coverUrl: c.imageUrl || null,
    isbn: c.isBook ? (c.ean || null) : null,
    optional: false,
    qty: c.qty,
    price: c.price,
    complementoId: c.itemId,
    ean: c.ean || null,
    vtexProductId: c.vtexProductId || null,
  }));

  return { litItems, didItems, papItems, cmpItems };
}

// ============= CARRINHO (VTEX) =============
// Monta o pedido da série pro botão "Comprar". O que vai pro checkout da
// VTEX são as linhas com EAN (ISBN dos livros), vtexProductId (papelaria
// buscada na VTEX) ou kitSkuId (kit de papelaria da VTEX, um por card) — o
// backend resolve cada uma pra SKU (POST /public/carrinho/vtex). O resto
// (papelaria do catálogo estático, livro sem ISBN) fica fora do carrinho e a
// família é avisada antes do redirect.
function buildFamilyCart(data, seriesIdx, schoolId) {
  const activeSeries = window.resolveSeriesList ? window.resolveSeriesList(data) : (window.SERIES_LIST || []);
  const seriesName = activeSeries[seriesIdx] || "";
  const { litItems, didItems, papItems, cmpItems } = famSeriesItems(data, seriesIdx, seriesName);
  const ean = (isbn) => (isbn ? String(isbn).replace(/\D/g, "") || null : null);

  const items = [
    ...litItems.map(it => ({
      srcKey: it.key,
      type: "literatura",
      id: it.bookId,
      ean: ean(it.isbn),
      title: it.title,
      quantity: 1,
      optional: it.optional,
      price: it.price,
    })),
    ...didItems.map(it => ({
      srcKey: it.key,
      type: "didatico",
      id: it.didaticoId,
      ean: ean(it.isbn),
      title: it.title,
      quantity: 1,
      optional: false,
      price: it.price,
    })),
    ...papItems.filter(kit => kit.kitSkuId).map(kit => ({
      srcKey: kit.key,
      type: "papelaria-kit",
      id: kit.key,
      ean: null,
      title: kit.kitName || kit.title,
      quantity: 1,
      optional: false,
      price: null,
      kitSkuId: kit.kitSkuId,
    })),
    ...papItems.filter(kit => !kit.kitSkuId).flatMap(kit => kit.contents.map(p => ({
      srcKey: kit.key,
      type: "papelaria",
      id: p.id,
      ean: null,
      title: p.title,
      quantity: p.qty,
      optional: false,
      price: p.price,
      kit: p.kit,
      brandIdx: p.brandIdx,
      partnerId: p.partnerId,
      model: p.model,
      unit: p.unit,
      vtexProductId: p.vtexProductId,
    }))),
    ...cmpItems.map(it => ({
      srcKey: it.key,
      type: "complemento",
      id: it.complementoId,
      ean: ean(it.ean),
      title: it.title,
      quantity: it.qty,
      optional: false,
      price: it.price,
      vtexProductId: it.ean ? null : it.vtexProductId,
    })),
  ];

  return {
    schoolId: schoolId || null,
    schoolName: data.schoolName || "",
    year: famSeason(data),
    seriesIdx,
    seriesName,
    items,
  };
}
window.buildFamilyCart = buildFamilyCart;

// ============= SACOLA (mais de um estudante, um carrinho só) =============
// A família monta a compra na home: em cada série, "Adicionar à sacola"
// guarda a lista como um estudante e volta pra home, onde ela escolhe a
// série do próximo. Na home, "Estudantes nesta compra" mostra cada um com
// série, encape e preço, e "Comprar para N" fecha tudo num carrinho só.
// Antes ela comprava uma série, voltava e comprava a outra — e com encape
// cada compra criava um orderForm novo, que trocava o carrinho da anterior.
// Fica no localStorage do navegador, por escola. Uma entrada por estudante
// (irmãos na mesma série = duas entradas):
// { id, seriesIdx, seriesName, encape, unselected, items, count, books }
// — items já no formato do POST /public/carrinho/vtex.
const FAM_SACOLA_PREFIX = "scolist:sacola:";
const famSacolaSubs = new Set();
function famSacolaRead(schoolId) {
  if (!schoolId) return [];
  try {
    const v = JSON.parse(window.localStorage.getItem(FAM_SACOLA_PREFIX + schoolId) || "[]");
    if (!Array.isArray(v)) return [];
    // Formato de 29/10/2026: uma entrada por série com `students`. Vira uma
    // entrada por estudante, com id estável (o "editar" depende dele).
    return v.filter(e => e && Array.isArray(e.items)).flatMap(e => {
      const { students, ...rest } = e;
      const n = Math.max(1, Math.min(9, students || 1));
      return Array.from({ length: n }, (_, i) => ({ ...rest, id: e.id && i === 0 ? e.id : `s${e.seriesIdx}-${i}` }));
    });
  } catch (e) { return []; }
}
function famSacolaWrite(schoolId, list) {
  if (!schoolId) return;
  try {
    if (list.length) window.localStorage.setItem(FAM_SACOLA_PREFIX + schoolId, JSON.stringify(list));
    else window.localStorage.removeItem(FAM_SACOLA_PREFIX + schoolId);
  } catch (e) { /* aba anônima/storage bloqueado: a sacola só não sobrevive ao reload */ }
  famSacolaSubs.forEach(fn => fn(schoolId, list));
}
function useFamSacola(schoolId) {
  const [list, setList] = useStateF(() => famSacolaRead(schoolId));
  useEffectF(() => {
    setList(famSacolaRead(schoolId));
    const onWrite = (id, next) => { if (id === schoolId) setList(next); };
    // Voltar do checkout (bfcache) ou outra aba: relê.
    const reread = () => setList(famSacolaRead(schoolId));
    const onShow = e => { if (e.persisted) reread(); };
    famSacolaSubs.add(onWrite);
    window.addEventListener("pageshow", onShow);
    window.addEventListener("storage", reread);
    return () => {
      famSacolaSubs.delete(onWrite);
      window.removeEventListener("pageshow", onShow);
      window.removeEventListener("storage", reread);
    };
  }, [schoolId]);
  return [list, next => famSacolaWrite(schoolId, next)];
}
// Série que a escola tirou ou renomeou depois de a família guardar: sai.
function famSacolaValid(list, activeSeries) {
  return list.filter(e => activeSeries[e.seriesIdx] === e.seriesName);
}
// Estudante que a família mandou editar na home: a série abre com a
// seleção dele e o botão salva por cima. Fica em memória (não na URL); a
// home zera ao abrir.
let famSacolaEditingId = null;
function famSacolaSetEditing(id) { famSacolaEditingId = id; }

// Chave da linha, igual à que o backend usa em `missing` e nos preços
// (FamilyCartService).
function famCartKey(it) {
  return it.ean ? `ean:${it.ean}` : it.vtexProductId ? `product:${it.vtexProductId}`
    : it.kitSkuId ? `kit:${it.kitSkuId}` : null;
}
// Só o que dá pra mandar pro carrinho (EAN, vtexProductId ou kitSkuId).
function famCartSendable(items) {
  return items.filter(famCartKey).map(it =>
    it.ean ? { ean: it.ean, quantity: it.quantity }
      : it.vtexProductId ? { vtexProductId: it.vtexProductId, quantity: it.quantity }
      : { kitSkuId: it.kitSkuId, quantity: it.quantity });
}

// Entrada da sacola pra uma série. unselected = chaves desmarcadas (null =
// padrão da página: tudo marcado, complementos desmarcados).
function famSacolaEntry(data, seriesIdx, schoolId, { unselected = null, encape = false, id = null } = {}) {
  const activeSeries = window.resolveSeriesList ? window.resolveSeriesList(data) : (window.SERIES_LIST || []);
  const seriesName = activeSeries[seriesIdx] || "";
  const { litItems, didItems, papItems, cmpItems } = famSeriesItems(data, seriesIdx, seriesName);
  const allKeys = [...litItems, ...didItems, ...papItems, ...cmpItems].map(it => it.key);
  const off = new Set(unselected ? unselected.filter(k => allKeys.includes(k)) : cmpItems.map(it => it.key));
  const cartItems = buildFamilyCart(data, seriesIdx, schoolId).items.filter(it => !off.has(it.srcKey));
  const books = [...litItems, ...didItems].filter(it => !off.has(it.key) && it.isbn).length;
  return {
    id: id || `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    seriesIdx, seriesName,
    encape: !!encape && books > 0,
    unselected: [...off],
    items: famCartSendable(cartItems),
    count: allKeys.length - off.size,
    books,
  };
}

// Linhas do POST, com o encape marcado por item (o backend soma o que
// repete entre estudantes, ver FamilyCartService).
function famSacolaItems(list, { skipEncape = false } = {}) {
  return list.flatMap(e => e.items.map(it => ({
    ...it,
    ...(e.encape && !skipEncape ? { encape: true } : {}),
  })));
}
const famSacolaBooks = list => list.reduce((n, e) => n + (e.encape ? (e.books || 0) : 0), 0);

// Compra da sacola inteira: uma chamada só, um carrinho só.
function useFamSacolaCheckout(schoolId) {
  const [state, setState] = useStateF({ status: "idle" });
  const runRef = React.useRef(0);
  const lastRef = React.useRef([]);
  useEffectF(() => {
    const onShow = (e) => { if (e.persisted) { runRef.current++; setState({ status: "idle" }); } };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);
  const run = async (list, { skipEncape = false } = {}) => {
    const r = ++runRef.current;
    const stale = () => r !== runRef.current;
    lastRef.current = list;
    const items = famSacolaItems(list, { skipEncape });
    const label = list.length === 1 ? `Lista do ${list[0].seriesName}` : `Listas de ${list.length} estudantes`;
    if (!items.length) {
      window.ScolistAnalytics.event("checkout_failed", { school_id: schoolId, reason: "empty" });
      setState({ status: "empty", label });
      return;
    }
    const books = skipEncape ? 0 : famSacolaBooks(list);
    const ga = { school_id: schoolId, students: list.length, item_count: items.length, encape_books: books };
    window.ScolistAnalytics.event("begin_checkout", ga);
    setState(books > 0
      ? { status: "working", phase: "encape", startedAt: Date.now(), books, label }
      : { status: "working", phase: "cart", label });
    try {
      const res = await window.ScolistAPI.buildFamilyCheckout(items, { schoolId });
      if (stale()) return;
      if (!res || !res.checkoutUrl) {
        window.ScolistAnalytics.event("checkout_failed", { ...ga, reason: "empty" });
        setState({ status: "empty", label });
        return;
      }
      if (books > 0 && !(res.encaped > 0)) {
        window.ScolistAnalytics.event("checkout_failed", { ...ga, reason: "encape" });
        setState({ status: "encape-failed", label });
        return;
      }
      // Última coisa que a gente vê da família: dali em diante é a loja.
      window.ScolistAnalytics.event("checkout_redirect", ga);
      setState({ status: "working", phase: "redirect", label });
      // Foi pro carrinho da loja: a sacola daqui já cumpriu o papel.
      famSacolaWrite(schoolId, []);
      window.location.href = res.checkoutUrl;
    } catch (e) {
      console.error("[lista-familia] falha ao montar carrinho VTEX (sacola)", e);
      if (!stale()) {
        window.ScolistAnalytics.event("checkout_failed", { ...ga, reason: "error" });
        setState({ status: "error", label });
      }
    }
  };
  return {
    state,
    busy: state.status === "working",
    run,
    retry: () => run(lastRef.current),
    skipEncape: () => run(lastRef.current, { skipEncape: true }),
    close: () => { runRef.current++; setState({ status: "idle" }); },
  };
}

// Mesmo id da escola nas duas telas: rota do link público (lista/<id>) ou
// escola da sessão no preview interno.
function famSchoolIdOf(basePath, account) {
  return (basePath.match(/^lista\/([^/]+)/) || [])[1]
    || (account && account.school && account.school.id) || null;
}

// ============= UI =============
function FamBadge({ children, tone = "neutral" }) {
  const tones = {
    neutral: { bg: "var(--paper-200)", fg: "var(--slate-600)" },
    teal:    { bg: "var(--teal-050)", fg: "var(--teal-700)" },
    amber:   { bg: "var(--sun-400)", fg: "var(--teal-700)" },
    lime:    { bg: "var(--green-050)", fg: "var(--green-700)" },
  };
  const t = tones[tone] || tones.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      fontSize: 12, fontWeight: 600, letterSpacing: "var(--ls-badge)",
      padding: "4px 11px", borderRadius: "var(--radius-pill)", lineHeight: 1.2,
      background: t.bg, color: t.fg, whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

// Faixa de cor full-bleed com conteúdo centrado — o ritmo vertical do Scolex
function FamBand({ tone = "white", children, style }) {
  const backgrounds = {
    white: "var(--surface-page)",
    mint: "var(--surface-sunken)",
    green: "var(--surface-band-green)",
    cream: "var(--surface-band-cream)",
  };
  return (
    <section style={{ background: backgrounds[tone] || backgrounds.white, padding: "34px 16px 38px", ...style }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>{children}</div>
    </section>
  );
}

function FamSectionHead({ eyebrow, title, tone = "teal" }) {
  const tones = { teal: "var(--teal-300)", green: "var(--green-600)", muted: "var(--slate-600)" };
  return (
    <div style={{ textAlign: "center", marginBottom: 18 }}>
      <div style={{ fontSize: title ? 11.5 : 17, fontWeight: 600, color: tones[tone] || tones.teal, letterSpacing: title ? "var(--ls-eyebrow)" : ".12em", textTransform: "uppercase" }}>{eyebrow}</div>
      {title && <h2 style={{ fontSize: "clamp(22px,6.4vw,26px)", fontWeight: 600, lineHeight: 1.18, letterSpacing: -.3, color: "var(--text-strong)", margin: "7px 0 0", textWrap: "pretty" }}>{title}</h2>}
    </div>
  );
}

function FamItemGlyph({ item, w = 70 }) {
  const isPap = item.kind === "pap";
  const isSquare = isPap || item.kind === "cmp";
  const count = item.contents ? item.contents.length : 1;
  return (
    <div style={{
      width: w, height: isSquare ? w : Math.round(w * 1.45), borderRadius: isSquare ? 14 : 4, flexShrink: 0,
      background: isPap ? "var(--sun-050)" : "var(--teal-050)",
      border: `1px solid ${isPap ? "var(--sun-100)" : "var(--teal-100)"}`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2,
      color: isPap ? "var(--sun-600)" : "var(--teal-500)",
      fontSize: 10, fontWeight: 600, letterSpacing: "var(--ls-badge)",
      textTransform: "uppercase", textAlign: "center", padding: 4,
    }}>
      {isPap ? (
        <>
          <span style={{ fontSize: Math.round(w * 0.3), letterSpacing: 0 }}>{count}</span>
          <span>{count === 1 ? "item" : "itens"}</span>
        </>
      ) : (
        <span style={{ lineHeight: 1.25 }}>{item.kind === "cmp" ? "Item" : "Didático"}</span>
      )}
    </div>
  );
}

function FamItemCard({ item, periodLabel, selected = true, onToggle = null }) {
  const hasContents = item.contents && item.contents.length > 0;

  return (
    <div className="fam-item" style={{
      background: "#fff", borderRadius: "var(--radius-card)",
      border: "1px solid var(--border-card)", boxShadow: "var(--shadow-card)", overflow: "hidden",
      position: "relative",
    }}>
      {onToggle && (
        <label className="no-print" title={selected ? "Tirar da compra" : "Incluir na compra"} style={{
          position: "absolute", top: 10, right: 10, zIndex: 1, padding: 4, cursor: "pointer", display: "flex",
        }}>
          <input type="checkbox" checked={selected} onChange={onToggle}
            aria-label={`${selected ? "Tirar" : "Incluir"} ${item.title} ${selected ? "da" : "na"} compra`}
            style={{ width: 22, height: 22, margin: 0, accentColor: "var(--teal-700)", cursor: "pointer" }}/>
        </label>
      )}
      <div className={selected ? "" : "fam-unselected"} style={{ display: "flex", gap: 14, padding: onToggle ? "14px 44px 14px 14px" : 14, alignItems: "flex-start" }}>
        {item.kind === "lit" ? (
          // capa em plate branco levemente girado, como as "polaroids" da trilha
          <div style={{
            flexShrink: 0, background: "#fff", padding: "6px 6px 8px", borderRadius: "var(--radius-sm)",
            boxShadow: "var(--shadow-cover)", transform: "rotate(-2deg)",
          }}>
            <BookCover book={item.book} size="sm"/>
          </div>
        ) : item.coverUrl ? (
          <img src={item.coverUrl} alt={item.title} loading="lazy" style={item.kind === "cmp" ? {
            // Foto de produto da loja (quadrada, fundo branco) — sem cortar
            width: 70, height: 70, objectFit: "contain", borderRadius: 14, background: "#fff",
            border: "1px solid var(--border-card)", flexShrink: 0,
          } : {
            width: 70, height: 101, objectFit: "cover", borderRadius: "var(--radius-cover)",
            boxShadow: "var(--shadow-cover)", flexShrink: 0,
          }}/>
        ) : (
          <FamItemGlyph item={item}/>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Sem selo "Obrigatório" (14/10/2026): o que não é
              marcado como opcional já é obrigatório por padrão. Sem selo
              "Fora da loja online" (21/10/2026). */}
          {item.kind !== "pap" && (item.optional || item.bimestre || item.lang) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 5 }}>
              {item.optional && <FamBadge tone="amber">Opcional</FamBadge>}
              {item.bimestre && <FamBadge tone="lime">{item.bimestre}º {periodLabel}</FamBadge>}
              {item.lang && <FamBadge>{item.lang}</FamBadge>}
            </div>
          )}

          <div style={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, letterSpacing: -.15, color: "var(--text-strong)", textWrap: "pretty" }}>
            {item.title}
          </div>
          {item.sub && <div style={{ fontSize: 12.5, color: "var(--text-body)", marginTop: 2 }}>{item.sub}</div>}
          {item.meta && item.meta.length > 0 && (
            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 3 }}>{item.meta.join(" · ")}</div>
          )}
          {item.isbn && (
            <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
              ISBN {item.isbn}
            </div>
          )}
        </div>
      </div>

      {/* Itens do kit sempre abertos, um por linha com a quantidade à
          direita — layout do protótipo da Joana (familia-ui.jsx, ItemCard). */}
      {hasContents && (
        <div style={{ borderTop: "1px solid var(--border-card)", padding: "10px 13px 12px" }}>
          <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid" }}>
            {item.contents.map((p, i) => (
              <li key={p.id || i} style={{
                display: "grid", gridTemplateColumns: "24px minmax(0,1fr) auto", alignItems: "baseline", gap: 10,
                padding: "7px 0", borderTop: i ? "1px dashed var(--border-card)" : "none",
                fontSize: 13.5, color: "var(--text-body)", lineHeight: 1.4,
              }}>
                <span aria-hidden="true" style={{ color: "var(--green-600)", fontWeight: 700, fontSize: 13, textAlign: "center" }}>✓</span>
                <span style={{ minWidth: 0 }}>
                  {p.title}
                  {p.sub && <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)" }}>{p.sub}</span>}
                </span>
                <span style={{ fontWeight: 600, color: "var(--text-strong)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{p.qty || 1} un.</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ============= PDF (lista de compras) =============
// O que sai no botão PDF / Ctrl+P: em vez do "print" da página (cards,
// capas, faixas coloridas), uma lista de compras enxuta com um quadradinho
// por item pra família ir marcando na papelaria. Só os itens marcados na
// página, sem preço, incluindo os que estão fora da loja online.
function FamPrintList({ seriesName, season, sections }) {
  const count = sections.reduce((n, s) => n + s.rows.length, 0);
  const box = (
    <span aria-hidden="true" style={{
      width: 13, height: 13, border: "1.5px solid #3b4a4d", borderRadius: 2,
      display: "block", marginTop: 1.5,
    }}/>
  );
  const row = (r, i) => (
    <li key={r.key || i} className="fam-print-row" style={{
      display: "grid", gridTemplateColumns: "20px minmax(0,1fr) auto", gap: 10, alignItems: "start",
      padding: "6px 0", borderTop: "1px solid #e3e7e8", fontSize: 12.5, lineHeight: 1.35,
    }}>
      {box}
      <span style={{ minWidth: 0 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "#14262a" }}>{r.title}</span>
        {r.tags.length > 0 && (
          <span style={{ fontSize: 10.5, color: "#3b4a4d", marginLeft: 6 }}>({r.tags.join(" · ")})</span>
        )}
        {r.details.length > 0 && (
          <span style={{ display: "block", fontSize: 10.5, color: "#5d6b6e", lineHeight: 1.4, marginTop: 1 }}>{r.details.join(" · ")}</span>
        )}
      </span>
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "#14262a", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        {r.qty} un.
      </span>
    </li>
  );

  return (
    <div className="fam-print-only" style={{ padding: "14px 0 0", color: "#14262a", fontFamily: "var(--font-core, system-ui, sans-serif)" }}>
      {/* Lex com a prancheta no canto do cabeçalho. Versão reduzida da
          lex-checklist.png pra não pesar o PDF. */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: -.3 }}>Lista de compras · {seriesName}</div>
            <div style={{ fontSize: 11.5, color: "#5d6b6e" }}>
              Lista {season} · {count} {count === 1 ? "item" : "itens"}
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: "#3b4a4d", marginTop: 10 }}>
            Estudante: <span style={{ display: "inline-block", width: 260, borderBottom: "1px solid #9aa6a8" }}/>
          </div>
        </div>
        <img src="assets/lex-checklist-print.png" alt="" aria-hidden="true" style={{ height: 96, width: "auto", flexShrink: 0, display: "block" }}/>
      </div>

      {sections.map(s => (
        <section key={s.title} style={{ marginTop: 16 }}>
          <div className="fam-print-head" style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase",
            color: "#1c5f66", paddingBottom: 4, borderBottom: "1.5px solid #1c5f66",
          }}>
            {s.title}
            <span style={{ fontWeight: 500, color: "#5d6b6e", marginLeft: 6 }}>{s.rows.length}</span>
          </div>
          {s.groups ? s.groups.map(g => (
            <div key={g.title}>
              <div className="fam-print-head" style={{ fontSize: 11.5, fontWeight: 600, color: "#3b4a4d", padding: "8px 0 2px" }}>{g.title}</div>
              <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>{g.rows.map(row)}</ol>
            </div>
          )) : (
            <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>{s.rows.map(row)}</ol>
          )}
        </section>
      ))}

      <p style={{ marginTop: 18, fontSize: 10, color: "#5d6b6e" }}>
        Você pode comprar onde preferir: os títulos e os ISBNs são os mesmos da lista da escola.
      </p>
    </div>
  );
}

// Linhas da lista impressa a partir dos itens da série (famSeriesItems).
function famPrintSections({ litItems, didItems, papItems, cmpItems }, isSelected, periodLabel) {
  const bookRow = it => ({
    key: it.key,
    title: it.title,
    tags: [it.optional ? "opcional" : null, it.bimestre ? `${it.bimestre}º ${periodLabel}` : null, it.lang].filter(Boolean),
    details: [it.sub, ...(it.kind === "lit" ? [it.book && it.book.editora] : it.meta || []), it.isbn ? `ISBN ${it.isbn}` : null].filter(Boolean),
    qty: 1,
  });
  const lit = litItems.filter(it => isSelected(it.key)).map(bookRow);
  const did = didItems.filter(it => isSelected(it.key)).map(bookRow);
  const papGroups = papItems.filter(it => isSelected(it.key)).map(kit => ({
    title: (FAM_KITS.find(k => "pap:" + k.id === kit.key) || {}).label || kit.title,
    rows: kit.contents.map((p, i) => ({ key: kit.key + ":" + i, title: p.title, tags: [], details: [p.sub].filter(Boolean), qty: p.qty || 1 })),
  }));
  const cmp = cmpItems.filter(it => isSelected(it.key)).map(it => ({
    key: it.key, title: it.title, tags: [],
    details: [it.sub, it.isbn ? `ISBN ${it.isbn}` : null].filter(Boolean),
    qty: it.qty || 1,
  }));
  return [
    lit.length && { title: "Literatura", rows: lit },
    did.length && { title: "Didáticos", rows: did },
    papGroups.length && { title: "Papelaria", rows: papGroups.flatMap(g => g.rows), groups: papGroups },
    cmp.length && { title: "Complementos", rows: cmp },
  ].filter(Boolean);
}

// Switch do protótipo da Joana (familia-ui.jsx, SwitchRow) — só o visual;
// quem usa é o <button role="switch"> em volta.
function FamSwitch({ on }) {
  return (
    <span aria-hidden="true" style={{
      width: 38, height: 22, borderRadius: 999, flexShrink: 0, position: "relative",
      background: on ? "var(--teal-400)" : "var(--slate-200)", transition: "background .16s",
    }}>
      <span style={{
        position: "absolute", top: 3, left: on ? 19 : 3,
        width: 16, height: 16, borderRadius: "50%", background: "#fff",
        transition: "left .16s", boxShadow: "0 1px 3px rgba(0,0,0,.2)",
      }}/>
    </span>
  );
}

function FamSwitchRow({ on, onToggle, title, sub, first, last, disabled }) {
  const radius = "var(--radius-card)";
  return (
    <button role="switch" aria-checked={on} onClick={onToggle} disabled={disabled} style={{
      display: "flex", alignItems: "center", gap: 11, width: "100%",
      padding: "13px 15px", textAlign: "left",
      borderTopLeftRadius: first ? radius : 0, borderTopRightRadius: first ? radius : 0,
      borderBottomLeftRadius: last ? radius : 0, borderBottomRightRadius: last ? radius : 0,
      background: on ? "var(--teal-050)" : "var(--surface-card)",
      border: `1px solid ${on ? "var(--teal-100)" : "var(--border-card)"}`,
      borderTopWidth: first ? 1 : 0,
      opacity: disabled ? .6 : 1,
      transition: "background 200ms cubic-bezier(.4,0,.2,1), border-color 200ms cubic-bezier(.4,0,.2,1)",
    }}>
      <FamSwitch on={on}/>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14.5, fontWeight: 600, color: "var(--text-strong)", lineHeight: 1.25 }}>{title}</span>
        {sub && <span style={{ display: "block", fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>{sub}</span>}
      </span>
    </button>
  );
}

// ============= INSTRUÇÕES DA ESCOLA (hub) =============
// Caixa "Antes de comprar" editada pelo time no console (SchoolFamilyNotice,
// schools/{id}.familyNotice): como comprar, datas importantes e cupons —
// dúvidas que chegavam no atendimento. Bloco vazio não aparece.
// topText/bottomText são avisos soltos fora da caixa: um logo abaixo do hero
// (ex.: data de corte do pedido) e outro depois da lista de séries
// (complemento ou disclaimer).
function famNoticeOf(notice) {
  const n = notice || {};
  const topText = String(n.topText || "").trim();
  const bottomText = String(n.bottomText || "").trim();
  const howToBuy = String(n.howToBuy || "").trim();
  const dates = (Array.isArray(n.dates) ? n.dates : []).filter(d => d && String(d.label || "").trim())
    .slice().sort((a, b) => String(a.date || "9999").localeCompare(String(b.date || "9999")));
  const coupons = (Array.isArray(n.coupons) ? n.coupons : []).filter(c => c && String(c.code || "").trim());
  return { topText, bottomText, howToBuy, dates, coupons, empty: !howToBuy && !dates.length && !coupons.length };
}

// Nota da série (schools/{id}.seriesNotices, área admin): aviso no topo da
// página de uma série. Casa pelo nome da série, não pelo índice.
function famSeriesNoteOf(notices, seriesName) {
  const hit = (Array.isArray(notices) ? notices : []).find(n => n && n.series === seriesName);
  return hit ? String(hit.text || "").trim() : "";
}

// Aviso do topo: faixa de destaque (borda âmbar) para o que a família não
// pode deixar passar.
function FamTopNotice({ text }) {
  return (
    <div role="note" style={{
      display: "flex", gap: 10, alignItems: "flex-start",
      padding: "12px 14px", borderRadius: "var(--radius-card)",
      background: "#FFF8E6", border: "1px solid #F3D58A", borderLeft: "4px solid var(--amber)",
      animation: "famRiseIn .28s ease both",
    }}>
      <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1.4 }}>⚠️</span>
      <p style={{ margin: 0, fontSize: 14.5, fontWeight: 500, color: "var(--text-strong)", lineHeight: 1.5, whiteSpace: "pre-line" }}>{text}</p>
    </div>
  );
}

function famNoticeDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return null;
  const d = new Date(+m[1], +m[2] - 1, +m[3]);
  return {
    day: m[3],
    month: d.toLocaleDateString("pt-BR", { month: "short" }).replace(".", ""),
  };
}

function FamCouponRow({ coupon, first }) {
  const [copied, setCopied] = useStateF(false);
  const code = String(coupon.code).trim();
  const copy = async () => {
    // Navegador embutido (Instagram/WhatsApp) costuma negar a Clipboard
    // API — cai no execCommand com um textarea temporário.
    let ok = false;
    try { await navigator.clipboard.writeText(code); ok = true; } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = code;
      ta.setAttribute("readonly", "");
      ta.style.cssText = "position:fixed;top:0;left:0;opacity:0";
      document.body.appendChild(ta);
      ta.select();
      try { ok = document.execCommand("copy"); } catch (e2) { ok = false; }
      ta.remove();
    }
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: first ? "none" : "1px dashed var(--border-card)" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: "inline-block", padding: "4px 10px", borderRadius: 8,
          border: "1.5px dashed var(--teal-300)", background: "var(--teal-050)", color: "var(--teal-700)",
          fontSize: 14.5, fontWeight: 700, letterSpacing: ".06em", fontVariantNumeric: "tabular-nums",
          wordBreak: "break-all",
        }}>{code}</span>
        {coupon.description && (
          <div style={{ fontSize: 13, color: "var(--text-body)", lineHeight: 1.45, marginTop: 5 }}>{coupon.description}</div>
        )}
      </div>
      <button onClick={copy} aria-label={`Copiar cupom ${code}`} style={{
        flexShrink: 0, padding: "7px 12px", borderRadius: "var(--radius-pill)", background: "#fff",
        border: "1.5px solid var(--teal-700)", color: "var(--teal-700)", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
      }}>
        {copied ? "Copiado ✓" : "Copiar"}
      </button>
    </div>
  );
}

function FamNoticeBox({ notice }) {
  const { howToBuy, dates, coupons } = notice;
  const label = text => (
    <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--slate-600)", marginBottom: 6 }}>{text}</div>
  );
  const blocks = [
    howToBuy && (
      <div key="how">
        {label("Como comprar")}
        <p style={{ margin: 0, fontSize: 14, color: "var(--text-body)", lineHeight: 1.55, whiteSpace: "pre-line" }}>{howToBuy}</p>
      </div>
    ),
    dates.length > 0 && (
      <div key="dates">
        {label("Datas importantes")}
        <div style={{ display: "grid" }}>
          {dates.map((d, i) => {
            const f = famNoticeDate(d.date);
            // Sem data: alinha o texto com os que têm o selo do dia.
            const pad = !f && dates.some(x => famNoticeDate(x.date));
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderTop: i ? "1px dashed var(--border-card)" : "none" }}>
                {f && (
                  <span style={{
                    width: 44, flexShrink: 0, textAlign: "center", borderRadius: 10, padding: "4px 0",
                    background: "var(--teal-050)", color: "var(--teal-700)", lineHeight: 1.1,
                  }}>
                    <span style={{ display: "block", fontSize: 17, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{f.day}</span>
                    <span style={{ display: "block", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>{f.month}</span>
                  </span>
                )}
                {pad && <span aria-hidden="true" style={{ width: 44, flexShrink: 0 }}/>}
                <span style={{ fontSize: 14, color: "var(--text-strong)", lineHeight: 1.4 }}>{d.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    ),
    coupons.length > 0 && (
      <div key="coupons">
        {label(coupons.length === 1 ? "Cupom" : "Cupons")}
        {coupons.map((c, i) => <FamCouponRow key={i} coupon={c} first={i === 0}/>)}
      </div>
    ),
  ].filter(Boolean);

  return (
    <div style={{
      background: "var(--surface-card)", borderRadius: "var(--radius-card)",
      border: "1px solid var(--border-card)", boxShadow: "var(--shadow-card)",
      animation: "famRiseIn .28s ease both",
    }}>
      <div style={{ padding: "14px 16px 4px", fontSize: 16, fontWeight: 600, color: "var(--text-strong)" }}>Antes de comprar</div>
      {blocks.map((b, i) => (
        <div key={i} style={{ padding: "12px 16px 14px", borderTop: i ? "1px solid var(--border-card)" : "none" }}>{b}</div>
      ))}
    </div>
  );
}

// ============= HUB (séries da escola) =============
// Ordem de exibição das séries: a mesma de data.seriesNames, que a escola
// define no Passo 1 (arrastando as séries). Antes a home reordenava por
// heurística de nome e ignorava a ordem escolhida pela escola.
function HubFamilia({ data, go, basePath = "familia" }) {
  // Mesma prioridade da logo (FamilyFrame): account.school no preview
  // interno, data.familyNotice (vindo da escola) no link público.
  const { account } = useApp();
  const notice = famNoticeOf((account && account.school && account.school.familyNotice) || data.familyNotice);
  // Séries de verdade da escola (Passo 1), não a lista fixa de 12.
  const activeSeries = window.resolveSeriesList ? window.resolveSeriesList(data) : (window.SERIES_LIST || []);
  const { label: periodLabel } = window.periodInfo ? window.periodInfo(data) : { label: "bimestre" };
  const counts = activeSeries.map((s, i) => {
    const { litItems, didItems, papItems, cmpItems } = famSeriesItems(data, i, s);
    return litItems.length + didItems.length + papItems.length + cmpItems.length;
  });

  // Sacola: estudantes desta compra (ver SACOLA acima).
  const schoolId = famSchoolIdOf(basePath, account);
  const [sacolaAll, saveSacola] = useFamSacola(schoolId);
  const sacola = famSacolaValid(sacolaAll, activeSeries);
  const sacolaCheckout = useFamSacolaCheckout(schoolId);
  const inSacola = sacola.reduce((m, e) => m.set(e.seriesIdx, (m.get(e.seriesIdx) || 0) + 1), new Map());
  useEffectF(() => { famSacolaSetEditing(null); }, []);
  useEffectF(() => { const img = new Image(); img.src = FAM_CHECKOUT_LEX; }, []);
  const updateEntry = (id, fn) => saveSacola(sacola.map(e => e.id === id ? fn(e) : e));
  // Trocar a série do estudante: lista nova inteira marcada, mantém o encape.
  const changeSeries = (id, idx) => updateEntry(id, e => famSacolaEntry(data, idx, schoolId, { encape: e.encape, id: e.id }));
  const editEntry = (e) => { famSacolaSetEditing(e.id); go(`${basePath}/serie/${e.seriesIdx}`); };
  const seriesListRef = React.useRef(null);
  // Altura da sacola fixa no rodapé: vira o respiro no fim da página.
  const [sheetH, setSheetH] = useStateF(0);
  const scrollToSeries = () => seriesListRef.current && seriesListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });

  // PDF da sacola: uma lista por estudante, cada uma na sua página.
  const printTitle = `Lista de compras - ${famSeason(data)}`;
  useEffectF(() => {
    let saved = null;
    const before = () => { saved = document.title; document.title = printTitle; };
    const after = () => { if (saved != null) { document.title = saved; saved = null; } };
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => { after(); window.removeEventListener("beforeprint", before); window.removeEventListener("afterprint", after); };
  }, [printTitle]);

  return (
    <FamilyFrame data={data} flush>
      {sacola.map((e, i) => {
        const off = new Set(e.unselected || []);
        return (
          <div key={e.id} className="fam-print-only" style={{ breakBefore: i ? "page" : "auto", pageBreakBefore: i ? "always" : "auto" }}>
            <FamPrintList seriesName={e.seriesName} season={famSeason(data)}
              sections={famPrintSections(famSeriesItems(data, e.seriesIdx, e.seriesName), k => !off.has(k), periodLabel)}/>
          </div>
        );
      })}
      <div className="no-print" style={{ paddingBottom: sacola.length ? sheetH : 0 }}>
      {/* Hero: gradiente da marca, Lex sangrando na borda direita */}
      <div style={{
        background: "linear-gradient(180deg, rgba(18,51,58,.18) 0%, rgba(18,51,58,.32) 100%), var(--hero-gradient)",
        color: "#fff", position: "relative", overflow: "hidden",
      }}>
        <div style={{ maxWidth: 560, margin: "0 auto", padding: "24px 16px 0", display: "flex", alignItems: "flex-end" }}>
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 22 }}>
            <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--text-on-dark-muted)" }}>
              Lista {famSeason(data)}
            </div>
            <h1 style={{
              fontSize: "clamp(26px,7.4vw,32px)", fontWeight: 600, lineHeight: 1.14,
              letterSpacing: -.4, margin: "9px 0 0", color: "#fff", textWrap: "balance",
            }}>
              A lista do volta às aulas já está pronta
            </h1>
          </div>
          <img src="assets/lex3.png" alt="Lex" style={{
            width: "clamp(136px,38vw,182px)", height: "auto", flexShrink: 0,
            marginRight: -22, marginLeft: -14, marginBottom: -6,
            animation: "famPopIn .45s cubic-bezier(.16,1,.3,1) both",
          }}/>
        </div>
      </div>

      {(notice.topText || !notice.empty) && (
        <FamBand tone="white" style={{ padding: "22px 16px 24px" }}>
          {notice.topText && <FamTopNotice text={notice.topText}/>}
          {!notice.empty && (
            <div style={{ marginTop: notice.topText ? 14 : 0 }}><FamNoticeBox notice={notice}/></div>
          )}
        </FamBand>
      )}

      <div ref={seriesListRef} style={{ scrollMarginTop: 8 }}>
      <FamBand tone="mint" style={{ padding: "30px 16px 34px" }}>
        {sacola.length > 0
          ? <FamSectionHead eyebrow="Outro estudante" title="Qual a série do próximo estudante?"/>
          : <FamSectionHead eyebrow="Escolha a série" title={`Qual a série do seu estudante em ${famSeason(data)}?`}/>}

        <div style={{ display: "grid", gap: 10 }}>
          {activeSeries.map((s, i) => (
            <button key={s + i} onClick={() => go(`${basePath}/serie/${i}`)}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-card-hover)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "var(--shadow-card)"; }}
              style={{
                display: "flex", alignItems: "center", gap: 13,
                padding: "15px 16px", borderRadius: "var(--radius-card)", textAlign: "left",
                background: "var(--surface-card)", border: "1px solid var(--border-card)", boxShadow: "var(--shadow-card)",
                transition: "transform 360ms cubic-bezier(.16,1,.3,1), box-shadow 200ms cubic-bezier(.4,0,.2,1)",
                animation: "famRiseIn .28s ease both", animationDelay: `${Math.min(i * 24, 260)}ms`,
              }}>
              <span style={{
                width: 40, height: 40, borderRadius: 999, flexShrink: 0,
                background: "var(--teal-400)", color: "#fff", boxShadow: "0 0 0 4px #fff, 0 0 0 5px var(--teal-100)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 15, fontWeight: 600,
              }}>{i + 1}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 16.5, fontWeight: 600, color: "var(--text-strong)", lineHeight: 1.25 }}>{s}</span>
                <span style={{ display: "block", fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>
                  {counts[i] === 0 ? "Lista em curadoria" : `${counts[i]} ${counts[i] === 1 ? "item na lista" : "itens na lista"}`}
                </span>
              </span>
              {inSacola.has(i) && <FamBadge tone="lime">{inSacola.get(i) > 1 ? `${inSacola.get(i)} na sacola` : "Na sacola"}</FamBadge>}
              <span style={{ color: "var(--teal-300)", fontSize: 18 }}>›</span>
            </button>
          ))}
        </div>

        {/* Com "Como comprar" preenchido no console, a instrução da escola
            substitui esta frase padrão. */}
        {!notice.howToBuy && (
          <p style={{ marginTop: 20, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, textAlign: "center" }}>
            Tem mais de um estudante na escola? Adicione a lista de cada um à sacola e compre tudo de uma vez.
          </p>
        )}

        {/* Texto do final (console): complemento ou disclaimer da escola. */}
        {notice.bottomText && (
          <p style={{ margin: "16px 0 0", fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, textAlign: "center", whiteSpace: "pre-line" }}>
            {notice.bottomText}
          </p>
        )}
      </FamBand>
      </div>
      </div>

      {sacola.length > 0 && (
        <FamSacolaPanel schoolId={schoolId} sacola={sacola} activeSeries={activeSeries} counts={counts}
          busy={sacolaCheckout.busy}
          onSeries={changeSeries}
          onEncape={(id, on) => {
            const e = sacola.find(x => x.id === id);
            window.ScolistAnalytics.event("set_encape", { school_id: schoolId, series_name: e && e.seriesName, encape: on ? "sim" : "nao" });
            updateEntry(id, x => ({ ...x, encape: on }));
          }}
          onRemove={id => {
            const e = sacola.find(x => x.id === id);
            window.ScolistAnalytics.event("remove_from_sacola", { school_id: schoolId, series_name: e && e.seriesName });
            saveSacola(sacola.filter(x => x.id !== id));
          }}
          onEdit={editEntry}
          onAddAnother={scrollToSeries}
          onBuy={() => sacolaCheckout.run(sacola)}
          onHeight={setSheetH}/>
      )}

      {sacolaCheckout.state.status !== "idle" && (
        <CheckoutOverlay state={sacolaCheckout.state} label={sacolaCheckout.state.label} onClose={sacolaCheckout.close}
          onRetry={sacolaCheckout.retry} onSkipEncape={sacolaCheckout.skipEncape}/>
      )}
    </FamilyFrame>
  );
}

// ============= SÉRIE (lista + PDF) =============
function SerieFamilia({ data, seriesIdx, go, basePath = "familia" }) {
  // Séries de verdade da escola (Passo 1), não a lista fixa de 12.
  const activeSeries = window.resolveSeriesList ? window.resolveSeriesList(data) : (window.SERIES_LIST || []);
  const seriesName = activeSeries[seriesIdx] || "";
  const { label: periodLabel } = window.periodInfo ? window.periodInfo(data) : { label: "bimestre" };
  const { litItems, didItems, papItems, cmpItems } = famSeriesItems(data, seriesIdx, seriesName);
  const total = litItems.length + didItems.length + papItems.length + cmpItems.length;
  const schoolName = data.schoolName || "Sua escola";
  // Link público: o id vem da própria rota (lista/<schoolId>); preview
  // interno: escola da sessão logada.
  const { account } = useApp();
  const schoolId = famSchoolIdOf(basePath, account);
  // Sacola: "Adicionar à sacola" guarda esta lista como um estudante e
  // volta pra home. Vindo do "editar" da home, abre com a seleção daquele
  // estudante e salva por cima.
  const [sacolaAll, saveSacola] = useFamSacola(schoolId);
  const sacola = famSacolaValid(sacolaAll, activeSeries);
  const [editingId] = useStateF(() => famSacolaEditingId);
  const editing = sacola.find(e => e.id === editingId && e.seriesIdx === seriesIdx) || null;
  // Link público traz as notas em data; preview interno, na escola da sessão.
  const seriesNote = famSeriesNoteOf(
    Array.isArray(data.seriesNotices) ? data.seriesNotices : (account && account.school && account.school.seriesNotices),
    seriesName);
  // Caixa "Antes de comprar" da escola também na série: quem chega pelo
  // link direto da série (/<slug>/<serie>) não passa pela home.
  const notice = famNoticeOf((account && account.school && account.school.familyNotice) || data.familyNotice);

  // Seleção de itens ("Selecionar todos"): tudo marcado por padrão; a
  // família desmarca o que já tem. A sacola leva só os marcados.
  // Kit de papelaria é selecionado inteiro. Complementos são opcionais:
  // começam desmarcados e o "Selecionar todos" não mexe neles.
  const baseKeys = [...litItems, ...didItems, ...papItems].map(it => it.key);
  const cmpKeys = cmpItems.map(it => it.key);
  const allKeys = [...baseKeys, ...cmpKeys];
  const allKeysSig = allKeys.join("|");
  const initialUnselected = () => editing && Array.isArray(editing.unselected)
    ? new Set(editing.unselected.filter(k => allKeys.includes(k)))
    : new Set(cmpKeys);
  const [unselected, setUnselected] = useStateF(initialUnselected);
  useEffectF(() => { setUnselected(initialUnselected()); }, [seriesIdx, allKeysSig]);
  const isSelected = key => !unselected.has(key);
  const toggleItem = key => setUnselected(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const selectedCount = allKeys.filter(isSelected).length;
  const allSelected = baseKeys.length > 0 && baseKeys.every(isSelected);
  const setAll = on => setUnselected(prev => {
    const next = new Set(cmpKeys.filter(k => prev.has(k)));
    if (!on) baseKeys.forEach(k => next.add(k));
    return next;
  });

  // Nome sugerido do arquivo ao salvar em PDF: o navegador usa o título da
  // página — troca só durante a impressão ("Lista de compras - 1º ano - 2026").
  const printTitle = `Lista de compras - ${seriesName} - ${famSeason(data)}`;
  useEffectF(() => {
    let saved = null;
    const before = () => { saved = document.title; document.title = printTitle; };
    const after = () => { if (saved != null) { document.title = saved; saved = null; } };
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => { after(); window.removeEventListener("beforeprint", before); window.removeEventListener("afterprint", after); };
  }, [printTitle]);

  // Guarda (ou atualiza) o estudante e volta pra home, onde a família
  // escolhe a série do próximo ou compra. Encape se escolhe na home.
  const addToSacola = () => {
    const entry = famSacolaEntry(data, seriesIdx, schoolId, {
      unselected: [...unselected],
      encape: editing ? editing.encape : false,
      id: editing ? editing.id : null,
    });
    window.ScolistAnalytics.event(editing ? "update_sacola" : "add_to_sacola", {
      school_id: schoolId, series_name: seriesName, item_count: entry.count, students: editing ? sacola.length : sacola.length + 1,
    });
    saveSacola(editing ? sacola.map(e => e.id === editing.id ? entry : e) : [...sacola, entry]);
    go(basePath);
    window.scrollTo(0, 0);
  };

  const renderList = (items) => (
    <div style={{ display: "grid", gap: 12 }}>
      {items.map(it => <FamItemCard key={it.key} item={it} periodLabel={periodLabel}
        selected={isSelected(it.key)} onToggle={() => toggleItem(it.key)}/>)}
    </div>
  );

  return (
    <FamilyFrame data={data} onBack={() => go(basePath)}>
      <FamPrintList seriesName={seriesName} season={famSeason(data)}
        sections={famPrintSections({ litItems, didItems, papItems, cmpItems }, isSelected, periodLabel)}/>
      <div className="no-print" style={{ paddingBottom: total > 0 ? "calc(132px + var(--safe-b))" : 0 }}>
        {/* ===== HERO ===== */}
        <div style={{
          background: "radial-gradient(120% 90% at 88% -15%, rgba(127,196,204,.30) 0%, transparent 60%), var(--hero-gradient)",
          color: "#fff", padding: "24px 16px 26px", position: "relative", overflow: "hidden",
        }}>
          <div style={{ maxWidth: 560, margin: "0 auto", display: "flex", alignItems: "flex-end" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 11.5, fontWeight: 600, letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase" }}>
                <span style={{ color: "var(--teal-100)" }}>Lista {famSeason(data)}</span>
                <span style={{ flex: 1, height: 1, background: "rgba(191,224,228,.32)" }}/>
              </div>
              <div style={{ fontSize: "clamp(28px,8.5vw,34px)", fontWeight: 700, lineHeight: 1.08, marginTop: 6, letterSpacing: -.8 }}>
                {seriesName}
              </div>
              <div style={{ fontSize: 13, opacity: .82, marginTop: 5 }}>
                {total} {total === 1 ? "item na lista" : "itens na lista"}
                {litItems.length > 0 && ` · ${litItems.length} livro${litItems.length !== 1 ? "s" : ""}`}
                {didItems.length > 0 && ` · ${didItems.length} didático${didItems.length !== 1 ? "s" : ""}`}
              </div>
            </div>
            <img src="assets/lex-checklist.png" alt="Lex" style={{
              width: "clamp(104px,30vw,138px)", height: "auto", flexShrink: 0,
              marginRight: -14, marginLeft: -6, marginBottom: -26,
              animation: "famPopIn .45s cubic-bezier(.16,1,.3,1) both",
            }}/>
          </div>
        </div>

        {/* Toggle "Selecionar todos" — layout do protótipo da Joana
            (familia-ui.jsx, ListControls/SwitchRow). O encape fica na
            sacola, por estudante. */}
        {(seriesNote || !notice.empty || total > 0) && (
          <FamBand tone="white" style={{ padding: "22px 16px 24px" }}>
            {seriesNote && <FamTopNotice text={seriesNote}/>}
            {!notice.empty && (
              <div style={{ marginTop: seriesNote ? 14 : 0 }}><FamNoticeBox notice={notice}/></div>
            )}
            {total > 0 && (
            <div className="no-print" style={{ marginTop: seriesNote || !notice.empty ? 14 : 0, borderRadius: "var(--radius-card)", boxShadow: "var(--shadow-card)", overflow: "hidden" }}>
              <FamSwitchRow first last on={allSelected} onToggle={() => setAll(!allSelected)}
                title="Selecionar todos os itens"
                sub={selectedCount === allKeys.length
                  ? `${allKeys.length} ${allKeys.length === 1 ? "item na lista" : "itens na lista"} · desmarque o que você já tem`
                  : `${selectedCount} de ${allKeys.length} ${allKeys.length === 1 ? "item selecionado" : "itens selecionados"}`}/>
            </div>
            )}
          </FamBand>
        )}

        {litItems.length > 0 && (
          <FamBand tone="mint">
            <FamSectionHead eyebrow="Literatura"/>
            {renderList(litItems)}
          </FamBand>
        )}

        {didItems.length > 0 && (
          <FamBand tone="white">
            <FamSectionHead eyebrow="Didático" tone="muted"/>
            {renderList(didItems)}
          </FamBand>
        )}

        {papItems.length > 0 && (
          <FamBand tone="green">
            <FamSectionHead eyebrow="Papelaria" tone="green"/>
            {renderList(papItems)}
          </FamBand>
        )}

        {cmpItems.length > 0 && (
          <FamBand tone={papItems.length > 0 ? "white" : "green"}>
            <FamSectionHead eyebrow="Complementos" tone={papItems.length > 0 ? "muted" : "green"}/>
            {renderList(cmpItems)}
          </FamBand>
        )}

        {total === 0 && (
          <FamBand tone="white">
            <div style={{ padding: 34, textAlign: "center", background: "var(--surface-card)", borderRadius: "var(--radius-card)", border: "1px dashed var(--border-card)", color: "var(--text-muted)", fontSize: 14 }}>
              A lista desta série ainda está sendo finalizada pela escola.
            </div>
          </FamBand>
        )}

        {total > 0 && (
          <FamBand tone="cream" style={{ padding: "26px 16px 30px" }}>
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, textAlign: "center" }}>
              Lista organizada pelo {schoolName}. Você pode comprar onde preferir, os títulos são os mesmos.
            </p>
          </FamBand>
        )}

        {/* ===== Barra fixa: PDF + adicionar à sacola ===== */}
        {total > 0 && (
          <FamBottomBar top={
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, lineHeight: 1.3 }}>
              <span style={{ flex: 1, minWidth: 0, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {selectedCount === total ? `${total} ${total === 1 ? "item" : "itens"}` : `${selectedCount} de ${total} itens`}
                {" · "}<b style={{ color: "var(--text-strong)", fontWeight: 600 }}>{seriesName}</b>
              </span>
              {sacola.length > 0 && !editing && (
                <button onClick={() => go(basePath)} style={{
                  flexShrink: 0, background: "none", border: "none", padding: 0,
                  color: "var(--teal-700)", fontSize: 13, fontWeight: 600,
                  textDecoration: "underline", textUnderlineOffset: 3,
                }}>
                  Sacola ({sacola.length})
                </button>
              )}
            </div>
          }>
              <button onClick={() => printFamList({ school_id: schoolId, origin: "serie", series_name: seriesName, item_count: selectedCount })} disabled={selectedCount === 0} style={{
                padding: "11px 16px", borderRadius: "var(--radius-pill)", background: "#fff",
                opacity: selectedCount === 0 ? .55 : 1,
                border: "1.5px solid var(--teal-700)", color: "var(--teal-700)",
                fontSize: 15, fontWeight: 600, whiteSpace: "nowrap",
              }}>
                Baixar PDF
              </button>
              <FamBuyButton onClick={addToSacola} disabled={selectedCount === 0} grow>
                {editing ? "Salvar na sacola" : "Adicionar à sacola"}
              </FamBuyButton>
          </FamBottomBar>
        )}
      </div>
    </FamilyFrame>
  );
}

// Barra fixa no rodapé (série e hub com sacola). `top`: linha fina acima
// dos botões.
function FamBottomBar({ top, children }) {
  return (
    <div className="no-print" style={{
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40,
      padding: "10px 16px calc(10px + var(--safe-b))",
      background: "var(--surface-card)", borderTop: "1px solid var(--border-card)",
    }}>
      {top && <div style={{ maxWidth: 560, margin: "0 auto 9px", paddingBottom: 9, borderBottom: "1px solid var(--border-card)" }}>{top}</div>}
      <div style={{ maxWidth: 560, margin: "0 auto", display: "flex", alignItems: "center", gap: 12 }}>
        {children}
      </div>
    </div>
  );
}

function FamBuyButton({ onClick, disabled, grow, children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      onMouseEnter={e => { e.currentTarget.style.background = "var(--action-bg-hover)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "var(--action-bg)"; }}
      style={{
        padding: "11px 22px", borderRadius: "var(--radius-pill)", border: "none",
        background: "var(--action-bg)", color: "var(--action-fg)", boxShadow: "var(--shadow-action)",
        fontSize: 15, fontWeight: 600, whiteSpace: "nowrap", transition: "background 120ms",
        opacity: disabled ? .55 : 1, ...(grow ? { flex: 1 } : {}),
      }}>
      {children}
    </button>
  );
}

// "Estudantes nesta compra" (home): um estudante por linha, com a série
// (dá pra trocar ali mesmo), encape e remover; no rodapé o PDF de todos e
// "Comprar para N". Sem valores: o preço a família vê no carrinho da loja. No celular cada linha quebra
// em duas (CSS .fam-sac-*). Fica fixa no rodapé, largura total; com
// muitos estudantes a lista rola dentro dela.
const FAM_SAC_AVATARS = ["var(--sun-400)", "#C4E6D8", "#F5CDC8", "var(--teal-100)"];
function FamSacolaPanel({ schoolId, sacola, activeSeries, counts, busy, onSeries, onEncape, onRemove, onEdit, onAddAnother, onBuy, onHeight }) {
  const rootRef = React.useRef(null);
  useEffectF(() => {
    const el = rootRef.current;
    if (!el || !onHeight) return;
    const report = () => onHeight(el.offsetHeight);
    report();
    if (!window.ResizeObserver) return;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const items = sacola.reduce((n, e) => n + (e.count || 0), 0);
  const n = sacola.length;
  const link = {
    background: "none", border: "none", padding: 0, color: "var(--teal-700)",
    fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3,
  };
  return (
    <div ref={rootRef} className="no-print" style={{
      position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40,
      background: "var(--surface-card)", borderTop: "1px solid var(--border-card)",
      boxShadow: "0 -8px 24px rgba(18,51,58,.10)",
      padding: "14px 16px calc(14px + var(--safe-b))",
    }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 10 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 17, fontWeight: 600, color: "var(--text-strong)", lineHeight: 1.25 }}>Estudantes nesta compra</div>
        <button onClick={onAddAnother} disabled={busy} style={{ ...link, fontSize: 14, whiteSpace: "nowrap" }}>+ Outro estudante</button>
      </div>

      <div className="fam-sac-rows">
      {sacola.map((e, i) => (
        <div key={e.id} className="fam-sac-row">
          <span className="fam-sac-av" style={{ background: FAM_SAC_AVATARS[i % FAM_SAC_AVATARS.length] }}>{i + 1}</span>
          <div className="fam-sac-main">
            <button onClick={() => onEdit(e)} disabled={busy} style={{
              background: "none", border: "none", padding: 0, textAlign: "left",
              fontSize: 16, fontWeight: 600, color: "var(--text-strong)", lineHeight: 1.25,
            }}>Estudante {i + 1}</button>
            <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2, display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 6 }}>
              <span className="fam-sac-series">
                {/* Texto visível + select transparente por cima: o select
                    nativo teria a largura da maior série. */}
                <span aria-hidden="true">{e.seriesName} <span style={{ fontSize: 10 }}>▾</span></span>
                <select value={e.seriesIdx} disabled={busy} aria-label={`Série do estudante ${i + 1}`}
                  onChange={ev => onSeries(e.id, parseInt(ev.target.value, 10))}>
                  {activeSeries.map((s, j) => (counts[j] > 0 || j === e.seriesIdx) && <option key={s + j} value={j}>{s}</option>)}
                </select>
              </span>
              <span>· {e.count} {e.count === 1 ? "item" : "itens"}</span>
              <span className="fam-sac-edit-inline">· <button onClick={() => onEdit(e)} disabled={busy} style={{ ...link, fontSize: 13 }}>editar</button></span>
            </div>
          </div>
          <button className="fam-sac-edit" onClick={() => onEdit(e)} disabled={busy} style={{ ...link, fontSize: 13 }}>editar itens</button>
          {e.books > 0 ? (
            <button className="fam-sac-enc" role="switch" aria-checked={!!e.encape} disabled={busy}
              onClick={() => onEncape(e.id, !e.encape)}>
              <FamSwitch on={!!e.encape}/>
              <span>Encapar</span>
            </button>
          ) : <span className="fam-sac-enc"/>}
          <button className="fam-sac-x" onClick={() => onRemove(e.id)} disabled={busy}
            aria-label={`Tirar o estudante ${i + 1} da compra`}>×</button>
        </div>
      ))}
      </div>

      <div className="fam-sac-foot">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-strong)", lineHeight: 1.3 }}>
            {n} {n === 1 ? "criança" : "crianças"}
          </div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 1 }}>
            {items} {items === 1 ? "item" : "itens"} no total
          </div>
        </div>
        <div className="fam-sac-foot-btns">
          <button onClick={() => printFamList({ school_id: schoolId, origin: "sacola", students: sacola.length })} disabled={busy} style={{
            padding: "11px 18px", borderRadius: "var(--radius-pill)", background: "#fff",
            border: "1.5px solid var(--teal-700)", color: "var(--teal-700)",
            fontSize: 15, fontWeight: 600, whiteSpace: "nowrap",
          }}>
            Baixar PDF
          </button>
          <FamBuyButton onClick={onBuy} disabled={busy}>
            {n === 1 ? "Comprar" : `Comprar para ${n}`}
          </FamBuyButton>
        </div>
      </div>
      </div>
    </div>
  );
}

// Overlay do "Comprar": cobre a página do clique até a loja abrir, com o
// que está acontecendo de verdade. Com encape, barra de progresso pelo
// tempo estimado (~1,15s por livro, ver FamilyCartService) — o backend não
// devolve progresso parcial.
const ENCAPE_SECS_PER_BOOK = 1.15;
// Lex do loading do "Comprar" — lex1 (156 KB); pré-carregado ao abrir a
// série (SerieFamilia) pra já estar em cache no clique.
const FAM_CHECKOUT_LEX = "assets/lex1.png";
function CheckoutOverlay({ state, label, onClose, onRetry, onSkipEncape }) {
  const working = state.status === "working";
  const [now, setNow] = useStateF(Date.now());
  useEffectF(() => {
    if (!working || state.phase !== "encape") return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [working, state.phase]);

  const estMs = (2 + (state.books || 1) * ENCAPE_SECS_PER_BOOK) * 1000;
  const elapsed = Math.max(0, now - (state.startedAt || now));
  const pct = Math.min(95, Math.round((elapsed / estMs) * 100));
  const remaining = Math.max(0, Math.ceil((estMs - elapsed) / 1000));

  const btn = (primary) => ({
    flex: 1, padding: "12px 16px", borderRadius: "var(--radius-pill)", fontSize: 15, fontWeight: 600,
    ...(primary
      ? { border: "none", background: "var(--action-bg)", color: "var(--action-fg)", boxShadow: "var(--shadow-action)" }
      : { background: "#fff", border: "1.5px solid var(--teal-700)", color: "var(--teal-700)" }),
  });

  let title, body, actions = null, spinner = false, progress = false;
  // Mesmo título com ou sem encape (14/10/2026): "Preparando carrinho",
  // não "Encapando N livros".
  if (working && state.phase === "encape") {
    title = "Preparando carrinho…";
    body = remaining > 0 ? `Pronto em uns ${remaining}s. Não feche esta página.` : "Quase lá…";
    progress = true;
  } else if (working) {
    title = state.phase === "redirect" ? "Abrindo a loja Scolados…" : "Preparando carrinho…";
    body = label;
    spinner = true;
  } else if (state.status === "encape-failed") {
    title = "Não conseguimos encapar agora";
    body = "Você pode seguir pro carrinho sem encape (e escolher o encape lá) ou tentar de novo.";
    actions = <>
      <button onClick={onRetry} style={btn(false)}>Tentar de novo</button>
      <button onClick={onSkipEncape} style={btn(true)}>Ir sem encape</button>
    </>;
  } else if (state.status === "empty") {
    title = "Esta lista ainda não está na loja online";
    body = "Nenhum item desta série está disponível na loja online ainda. Baixe o PDF e compre onde preferir.";
    actions = <button onClick={onClose} style={btn(true)}>Voltar</button>;
  } else {
    title = "Não conseguimos abrir o carrinho";
    body = "Tente de novo em instantes. Se continuar, baixe o PDF e compre onde preferir.";
    actions = <>
      <button onClick={onClose} style={btn(false)}>Voltar</button>
      <button onClick={onRetry} style={btn(true)}>Tentar de novo</button>
    </>;
  }

  return (
    <div className="no-print" style={{
      position: "fixed", inset: 0, zIndex: 50, background: "rgba(18,51,58,.55)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div role="dialog" aria-modal="true" aria-live="polite" style={{
        width: "100%", maxWidth: 400, background: "#fff", borderRadius: "var(--radius-card)",
        padding: "26px 22px 22px", textAlign: "center", boxShadow: "var(--shadow-card-hover)",
        animation: "famPopIn .22s cubic-bezier(.16,1,.3,1) both",
      }}>
        {working && (
          <img src={FAM_CHECKOUT_LEX} alt="" aria-hidden="true" className="fam-bob" style={{
            display: "block", width: 104, height: "auto", margin: "-6px auto 10px",
            animation: "famBob 1.6s ease-in-out infinite",
          }}/>
        )}
        {spinner && <div className="fam-spin" aria-hidden="true" style={{
          width: 34, height: 34, margin: "0 auto 14px", borderRadius: "50%",
          border: "3px solid var(--teal-100)", borderTopColor: "var(--teal-700)",
          animation: "famSpin .8s linear infinite",
        }}/>}
        <div style={{ fontSize: 19, fontWeight: 600, color: "var(--text-strong)", lineHeight: 1.25 }}>{title}</div>
        <p style={{ margin: "8px 0 0", fontSize: 14, color: "var(--text-muted)", lineHeight: 1.5 }}>{body}</p>
        {progress && (
          <div aria-hidden="true" style={{ marginTop: 16, height: 8, borderRadius: 999, background: "var(--teal-100)", overflow: "hidden" }}>
            <div style={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: "var(--teal-700)", transition: "width 250ms linear" }}/>
          </div>
        )}
        {working && (
          <button onClick={onClose} style={{ marginTop: 16, background: "none", border: "none", color: "var(--teal-700)", fontSize: 14, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>
            Cancelar
          </button>
        )}
        {actions && <div style={{ display: "flex", gap: 10, marginTop: 18 }}>{actions}</div>}
      </div>
    </div>
  );
}

const SUBJECT_LABELS = {
  portugues: "Português", matematica: "Matemática", ciencias: "Ciências",
  historia: "História", geografia: "Geografia", ingles: "Inglês",
  arte: "Arte", filosofia: "Filosofia", sociologia: "Sociologia",
  fisica: "Física", quimica: "Química", biologia: "Biologia",
  espanhol: "Espanhol", frances: "Francês", alemao: "Alemão",
  sistema: "Sistema de ensino",
};
function subjectLabel(s) { return SUBJECT_LABELS[s] || s; }

// ============= LINK PÚBLICO: estados de carregamento/erro =============
// Usado pelo app.jsx enquanto busca a lista pelo link público (#lista/:id)
// — antes dos dados chegarem, ou quando a escola/lista não existe. Não usa
// FamilyFrame porque ainda não tem schoolName/accent resolvidos nesse ponto.
function PublicListStatus({ status }) {
  const isLoading = status !== "not-found";
  return (
    <div className="fam-ds" style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 14, textAlign: "center", padding: 24, background: "var(--surface-sunken)",
    }}>
      <style>{FAM_DS_CSS}</style>
      {isLoading ? (
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-muted)" }}>Carregando a lista da escola…</div>
      ) : (
        <>
          <img src="assets/lex2.png" alt="Lex" style={{ width: 120, height: "auto" }}/>
          <div style={{ fontSize: 20, fontWeight: 600, color: "var(--text-strong)" }}>Não encontramos essa lista</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", maxWidth: 360, lineHeight: 1.5 }}>
            O link pode estar incompleto, ou a escola ainda não começou a montar a lista. Confira o link direto com a coordenação.
          </div>
        </>
      )}
    </div>
  );
}
window.PublicListStatus = PublicListStatus;

// ============= ROUTER =============
// Returns a React element for a family route, or null if hash doesn't match.
// basePath default ("familia") é o preview interno usado durante a edição
// (sessão logada, dados em memória — ver app.jsx). O link público pras
// famílias (#lista/:schoolId, ver Step13_Link) chama isso com
// basePath="lista/<schoolId>" pra navegar dentro do próprio link público em
// vez de cair no preview interno.
function renderFamiliaRoute(hash, data, setHash, basePath = "familia") {
  const go = (path) => setHash(path);
  if (hash === basePath || hash === basePath + "/") {
    return <HubFamilia data={data} go={go} basePath={basePath}/>;
  }
  const escapedBase = basePath.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const m = hash.match(new RegExp(`^${escapedBase}/serie/(\\d+)$`));
  if (m) {
    const idx = parseInt(m[1], 10);
    return <SerieFamilia data={data} seriesIdx={idx} go={go} basePath={basePath}/>;
  }
  return null;
}

// ============= ÁREA ADMIN (#admin) =============
// Só o time Scolados (adm-system). Primeira tela: notas do link das famílias
// por escola (schools/{id}.familyNotice) — aviso no topo, como comprar,
// datas, cupons e texto no final — e notas por série
// (schools/{id}.seriesNotices), com prévia de como a família vê. Substitui
// o card do console antigo (que continua lá até ser desligado; os dois
// editam o mesmo campo). Salva pelo PUT /admin/school/:id/family-notice, que
// confere o papel no token.
const ADMIN_EMPTY_NOTICE = { topText: "", howToBuy: "", dates: [], coupons: [], bottomText: "" };

function adminNoticeDraft(n) {
  const x = n || {};
  return {
    topText: x.topText || "",
    howToBuy: x.howToBuy || "",
    dates: Array.isArray(x.dates) ? x.dates.map(d => ({ date: d.date || "", label: d.label || "" })) : [],
    coupons: Array.isArray(x.coupons) ? x.coupons.map(c => ({ code: c.code || "", description: c.description || "" })) : [],
    bottomText: x.bottomText || "",
  };
}

// Quantos blocos aparecem pra família (pra lista de escolas).
function adminNoticeBlocks(n) {
  const f = famNoticeOf(n);
  return [f.topText, f.howToBuy, f.dates.length, f.coupons.length, f.bottomText].filter(Boolean).length;
}

// Notas por série no rascunho: { [nome da série]: texto } — vira
// [{ series, text }] ao salvar (só as preenchidas).
function adminSeriesDraft(list) {
  const out = {};
  (Array.isArray(list) ? list : []).forEach(n => { if (n && n.series) out[n.series] = n.text || ""; });
  return out;
}
function adminSeriesList(map) {
  return Object.keys(map).filter(k => String(map[k] || "").trim()).map(k => ({ series: k, text: map[k] }));
}
function adminSeriesCount(school) {
  return (Array.isArray(school.seriesNotices) ? school.seriesNotices : []).filter(n => n && String(n.text || "").trim()).length;
}
function adminDraftOf(school) {
  return { ...adminNoticeDraft(school.familyNotice), series: adminSeriesDraft(school.seriesNotices) };
}
// Assinatura pra "alterações não salvas": nota de série aberta e vazia não conta.
function adminDraftSig(draft) {
  return JSON.stringify({ ...draft, series: adminSeriesList(draft.series) });
}

function adminWhen(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }).replace(".", "")
    + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

const adminInput = {
  width: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 10,
  border: "1px solid var(--border-card)", background: "#fff", fontSize: 14,
  color: "var(--text-strong)", fontFamily: "inherit", lineHeight: 1.45,
};

function AdminField({ label, hint, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--slate-600)" }}>{label}</div>
      {hint && <div style={{ fontSize: 12.5, color: "var(--text-muted)", margin: "3px 0 7px", lineHeight: 1.45 }}>{hint}</div>}
      <div style={{ marginTop: hint ? 0 : 7 }}>{children}</div>
    </div>
  );
}

function AdminRowButton({ onClick, label, children }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label} style={{
      flexShrink: 0, width: 34, height: 34, borderRadius: 9, border: "1px solid var(--border-card)",
      background: "#fff", color: "var(--slate-600)", fontSize: 14, cursor: "pointer",
    }}>{children}</button>
  );
}

function AdminAddButton({ onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      marginTop: 4, padding: "6px 12px", borderRadius: "var(--radius-pill)", background: "#fff",
      border: "1.5px dashed var(--teal-200)", color: "var(--teal-700)", fontSize: 13, fontWeight: 600, cursor: "pointer",
    }}>{children}</button>
  );
}

function AdminNoticeEditor({ school, onBack, onSaved }) {
  const [draft, setDraft] = useStateF(() => adminDraftOf(school));
  const [saved, setSaved] = useStateF(() => adminDraftSig(adminDraftOf(school)));
  const [status, setStatus] = useStateF({ kind: "idle" }); // idle | saving | saved | error
  const [publicUrl, setPublicUrl] = useStateF(null);
  const dirty = adminDraftSig(draft) !== saved;
  // Séries da escola, do mesmo jeito que o link público monta a lista
  // (resolveSeriesList em cima do progresso salvo). null = carregando;
  // { error } = escola sem lista no assistente.
  const [seriesNames, setSeriesNames] = useStateF(null);
  const [openSeries, setOpenSeries] = useStateF(null); // série com o editor aberto (e na prévia)

  useEffectF(() => {
    let cancel = false;
    const isLocal = /^(localhost|127\.0\.0\.1|\[::1\])$|\.localhost$/.test(window.location.hostname);
    const origin = isLocal || typeof PUBLIC_LIST_ORIGIN === "undefined" ? window.location.origin : PUBLIC_LIST_ORIGIN;
    window.ScolistAPI.getPublicSlug(school.id)
      .then(slug => { if (!cancel) setPublicUrl(slug ? `${origin}/${slug}` : `${origin}/#lista/${school.id}`); })
      .catch(() => { if (!cancel) setPublicUrl(`${origin}/#lista/${school.id}`); });
    return () => { cancel = true; };
  }, [school.id]);

  useEffectF(() => {
    let cancel = false;
    window.ScolistAPI.getPublicFamilyList(school.id)
      .then(res => {
        if (cancel) return;
        const names = window.resolveSeriesList ? window.resolveSeriesList((res && res.data) || {}) : [];
        setSeriesNames([...new Set(names)]);
      })
      .catch(() => { if (!cancel) setSeriesNames({ error: true }); });
    return () => { cancel = true; };
  }, [school.id]);

  // Saindo com alteração não salva: confirma (botão voltar e fechar aba).
  useEffectF(() => {
    if (!dirty) return undefined;
    const onLeave = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);
  const back = () => {
    if (dirty && !window.confirm("Há alterações não salvas nas notas desta escola. Sair mesmo assim?")) return;
    onBack();
  };

  const edit = (patch) => { setDraft(d => ({ ...d, ...patch })); setStatus({ kind: "idle" }); };
  const editRow = (field, i, patch) => edit({ [field]: draft[field].map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const removeRow = (field, i) => edit({ [field]: draft[field].filter((_, j) => j !== i) });
  const editSeries = (name, text) => edit({ series: { ...draft.series, [name]: text } });
  const removeSeries = (name) => {
    const next = { ...draft.series };
    delete next[name];
    edit({ series: next });
  };

  const save = async () => {
    setStatus({ kind: "saving" });
    try {
      // Sem `{ series, ...resto }`: o Babel gera um `var _excluded` global e
      // os scripts clássicos de outros arquivos sobrescrevem.
      const familyNotice = adminNoticeDraft(draft);
      const out = await window.ScolistAPI.saveFamilyNotice(school.id, familyNotice, adminSeriesList(draft.series));
      const next = adminDraftOf(out);
      setDraft(next);
      setSaved(adminDraftSig(next));
      setStatus({ kind: "saved" });
      onSaved(school.id, out);
    } catch (e) {
      // 401/403 também mostram o toast de sessão (AuthErrorToast, app.jsx).
      setStatus({ kind: "error", message: e && e.status === 403 ? "Sua conta não tem permissão para editar as notas." : (e && e.message) || "Não foi possível salvar." });
    }
  };

  const preview = famNoticeOf(draft);
  const hasPreview = preview.topText || !preview.empty || preview.bottomText;

  // Notas de série que não está mais na lista (renomeada ou removida no
  // assistente): a família não vê; ficam aqui pra apagar ou recriar.
  const knownSeries = Array.isArray(seriesNames) ? seriesNames : [];
  const orphanSeries = Array.isArray(seriesNames) ? Object.keys(draft.series).filter(k => !knownSeries.includes(k)) : [];
  const previewSeries = openSeries && String(draft.series[openSeries] || "").trim() ? openSeries : null;

  return (
    <div>
      <button type="button" onClick={back} style={{ background: "none", border: "none", padding: 0, color: "var(--teal-700)", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
        ← Todas as escolas
      </button>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", marginTop: 10 }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-strong)", letterSpacing: -.3 }}>{school.name}</h2>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 3 }}>
            Ano letivo {school.season || "—"}
            {school.familyNoticeUpdatedAt && ` · notas editadas em ${adminWhen(school.familyNoticeUpdatedAt)}${school.familyNoticeUpdatedBy && school.familyNoticeUpdatedBy.email ? ` por ${school.familyNoticeUpdatedBy.email}` : ""}`}
          </div>
        </div>
        {publicUrl && (
          <a href={publicUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--teal-700)" }}>
            Abrir link das famílias ↗
          </a>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 24, marginTop: 18, alignItems: "start" }}>
        {/* ===== Editor ===== */}
        <div style={{ background: "#fff", borderRadius: "var(--radius-card)", border: "1px solid var(--border-card)", boxShadow: "var(--shadow-card)", padding: "4px 18px 18px" }}>
          <AdminField label="Aviso no topo" hint="Faixa de destaque no começo da página. Ex.: data de corte do pedido.">
            <textarea rows={2} value={draft.topText} onChange={e => edit({ topText: e.target.value })} style={adminInput}
              placeholder="Ex.: Pedidos com entrega na escola até 15/12. Depois disso, entrega em casa."/>
          </AdminField>

          <AdminField label="Como comprar" hint="Substitui a frase padrão sobre mais de um estudante.">
            <textarea rows={3} value={draft.howToBuy} onChange={e => edit({ howToBuy: e.target.value })} style={adminInput}
              placeholder="Ex.: Tem mais de um estudante na escola? Abra a série de cada um e finalize uma compra por estudante."/>
          </AdminField>

          <AdminField label="Datas importantes">
            {draft.dates.map((d, i) => (
              <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                <input type="date" value={d.date} onChange={e => editRow("dates", i, { date: e.target.value })} aria-label="Data" style={{ ...adminInput, width: 150, flexShrink: 0 }}/>
                <input value={d.label} onChange={e => editRow("dates", i, { label: e.target.value })} aria-label="Descrição da data" style={{ ...adminInput, width: "auto", flex: "1 1 180px" }}
                  placeholder="Ex.: Entrega dos materiais na escola"/>
                <AdminRowButton onClick={() => removeRow("dates", i)} label="Remover data">✕</AdminRowButton>
              </div>
            ))}
            <AdminAddButton onClick={() => edit({ dates: [...draft.dates, { date: "", label: "" }] })}>+ Adicionar data</AdminAddButton>
          </AdminField>

          <AdminField label="Cupons">
            {draft.coupons.map((c, i) => (
              <div key={i} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                <input value={c.code} onChange={e => editRow("coupons", i, { code: e.target.value.toUpperCase() })} aria-label="Código do cupom"
                  style={{ ...adminInput, width: 130, flexShrink: 0, fontWeight: 700, letterSpacing: ".05em" }} placeholder="CÓDIGO"/>
                <input value={c.description} onChange={e => editRow("coupons", i, { description: e.target.value })} aria-label="Descrição do cupom" style={{ ...adminInput, width: "auto", flex: "1 1 180px" }}
                  placeholder="Ex.: 10% de desconto em livros até 31/01"/>
                <AdminRowButton onClick={() => removeRow("coupons", i)} label="Remover cupom">✕</AdminRowButton>
              </div>
            ))}
            <AdminAddButton onClick={() => edit({ coupons: [...draft.coupons, { code: "", description: "" }] })}>+ Adicionar cupom</AdminAddButton>
          </AdminField>

          <AdminField label="Texto no final" hint="Depois da lista de séries. Ex.: aviso sobre preços ou marcas equivalentes.">
            <textarea rows={2} value={draft.bottomText} onChange={e => edit({ bottomText: e.target.value })} style={adminInput}
              placeholder="Ex.: Preços e disponibilidade podem mudar sem aviso."/>
          </AdminField>

          <AdminField label="Notas por série" hint="Aviso no topo da página de uma série. Ex.: material de artes comprado direto na escola.">
            {seriesNames === null && <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Carregando séries…</div>}
            {seriesNames && seriesNames.error && (
              <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.45 }}>Esta escola ainda não tem lista salva no assistente — as séries aparecem aqui quando tiver.</div>
            )}
            {knownSeries.length > 0 && (
              <div style={{ border: "1px solid var(--border-card)", borderRadius: 10, overflow: "hidden" }}>
                {knownSeries.map((name, i) => {
                  const text = draft.series[name] || "";
                  const open = openSeries === name || (name in draft.series);
                  return (
                    <div key={name} style={{ padding: "9px 11px", borderTop: i ? "1px solid var(--border-card)" : "none", background: text.trim() ? "var(--teal-050)" : "#fff" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}>{name}</span>
                        {open ? (
                          <AdminRowButton onClick={() => { removeSeries(name); if (openSeries === name) setOpenSeries(null); }} label={`Remover nota do ${name}`}>✕</AdminRowButton>
                        ) : (
                          <button type="button" onClick={() => { editSeries(name, ""); setOpenSeries(name); }} style={{
                            background: "none", border: "none", padding: "4px 0", color: "var(--teal-700)", fontSize: 13, fontWeight: 600, cursor: "pointer",
                          }}>+ Nota</button>
                        )}
                      </div>
                      {open && (
                        <textarea rows={2} value={text} autoFocus={openSeries === name && !text}
                          onFocus={() => setOpenSeries(name)}
                          onChange={e => editSeries(name, e.target.value)}
                          aria-label={`Nota do ${name}`} style={{ ...adminInput, marginTop: 7 }}
                          placeholder="Ex.: O kit de artes desta série é comprado direto na escola."/>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {orphanSeries.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12.5, color: "#8a5a00", lineHeight: 1.45, marginBottom: 6 }}>
                  Notas de séries que não estão mais na lista (renomeadas ou removidas). A família não vê.
                </div>
                {orphanSeries.map(name => (
                  <div key={name} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6 }}>
                    <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: "var(--text-body)", lineHeight: 1.45 }}>
                      <b>{name}:</b> {draft.series[name]}
                    </div>
                    <AdminRowButton onClick={() => removeSeries(name)} label={`Apagar nota do ${name}`}>✕</AdminRowButton>
                  </div>
                ))}
              </div>
            )}
          </AdminField>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 22, flexWrap: "wrap" }}>
            {status.kind === "saved" && !dirty && <span style={{ fontSize: 13, color: "var(--green-700)", fontWeight: 600 }}>Notas salvas ✓</span>}
            {status.kind === "error" && <span role="alert" style={{ fontSize: 13, color: "#b42318", fontWeight: 600 }}>{status.message}</span>}
            {dirty && status.kind !== "error" && <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>Alterações não salvas</span>}
            <button type="button" onClick={save} disabled={!dirty || status.kind === "saving"} style={{
              padding: "10px 20px", borderRadius: "var(--radius-pill)", border: "none",
              background: "var(--teal-700)", color: "#fff", fontSize: 14.5, fontWeight: 600,
              cursor: !dirty || status.kind === "saving" ? "default" : "pointer",
              opacity: !dirty || status.kind === "saving" ? .5 : 1,
            }}>{status.kind === "saving" ? "Salvando…" : "Salvar notas"}</button>
          </div>
        </div>

        {/* ===== Prévia ===== */}
        <div style={{ position: "sticky", top: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--slate-600)", marginBottom: 8 }}>
            Prévia · página inicial do link
          </div>
          <div style={{ background: "var(--surface-page)", borderRadius: "var(--radius-card)", border: "1px dashed var(--teal-200)", padding: 16 }}>
            {!hasPreview && (
              <div style={{ fontSize: 13.5, color: "var(--text-muted)", textAlign: "center", padding: "20px 8px", lineHeight: 1.55 }}>
                Nada preenchido: a família vê só a lista de séries, com a frase padrão sobre mais de um estudante.
              </div>
            )}
            {preview.topText && <FamTopNotice text={preview.topText}/>}
            {!preview.empty && <div style={{ marginTop: preview.topText ? 14 : 0 }}><FamNoticeBox notice={preview}/></div>}
            {hasPreview && (
              <div style={{ margin: "14px 0 0", padding: "12px", borderRadius: 12, background: "var(--surface-sunken)", textAlign: "center", fontSize: 12.5, color: "var(--text-muted)" }}>
                (lista de séries)
                {!preview.howToBuy && <div style={{ marginTop: 6 }}>Tem mais de um estudante na escola? Abra uma série por vez.</div>}
              </div>
            )}
            {preview.bottomText && (
              <p style={{ margin: "14px 0 0", fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6, textAlign: "center", whiteSpace: "pre-line" }}>{preview.bottomText}</p>
            )}
          </div>

          {previewSeries && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--slate-600)", margin: "18px 0 8px" }}>
                Prévia · página do {previewSeries}
              </div>
              <div style={{ background: "var(--surface-page)", borderRadius: "var(--radius-card)", border: "1px dashed var(--teal-200)", padding: 16 }}>
                <FamTopNotice text={String(draft.series[previewSeries]).trim()}/>
                <div style={{ margin: "14px 0 0", padding: "12px", borderRadius: 12, background: "var(--surface-sunken)", textAlign: "center", fontSize: 12.5, color: "var(--text-muted)" }}>
                  (itens da lista)
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminArea({ account }) {
  const [schools, setSchools] = useStateF(null);
  const [error, setError] = useStateF(null);
  const [query, setQuery] = useStateF("");
  const [onlyFilled, setOnlyFilled] = useStateF(false);
  const [selectedId, setSelectedId] = useStateF(null);

  const load = () => {
    setError(null);
    window.ScolistAPI.listAdminSchools()
      .then(setSchools)
      .catch(e => setError((e && e.message) || "Não foi possível carregar as escolas."));
  };
  useEffectF(load, []);

  const onSaved = (id, { familyNotice, seriesNotices }) => setSchools(list => list && list.map(s => s.id === id ? {
    ...s, familyNotice, seriesNotices, familyNoticeUpdatedAt: new Date().toISOString(),
    familyNoticeUpdatedBy: { email: account && account.user && account.user.email },
  } : s));

  const norm = t => String(t || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  const shown = (schools || [])
    .filter(s => !query.trim() || norm(s.name).includes(norm(query.trim())))
    .filter(s => !onlyFilled || adminNoticeBlocks(s.familyNotice) + adminSeriesCount(s) > 0);
  const selected = selectedId && (schools || []).find(s => s.id === selectedId);
  const filledCount = (schools || []).filter(s => adminNoticeBlocks(s.familyNotice) + adminSeriesCount(s) > 0).length;

  return (
    <div className="fam-ds" style={{ minHeight: "100vh", background: "var(--paper-100)" }}>
      <style>{FAM_DS_CSS}</style>
      <div style={{ background: "var(--teal-900)", color: "#fff" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}>
          <img src="assets/scolados-logo.png" alt="Scolados" style={{ height: 18, filter: "brightness(0) invert(1)" }}/>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--teal-100)" }}>Admin</span>
          <a href="#" style={{ marginLeft: "auto", fontSize: 13, fontWeight: 600, color: "#fff", textDecoration: "none" }}>← Voltar ao assistente</a>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "26px 16px 60px" }}>
        {selected ? (
          <AdminNoticeEditor key={selected.id} school={selected} onBack={() => setSelectedId(null)} onSaved={onSaved}/>
        ) : (
          <>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: "var(--text-strong)", letterSpacing: -.4 }}>Notas das listas</h1>
            <p style={{ margin: "6px 0 0", fontSize: 14, color: "var(--text-muted)", lineHeight: 1.55, maxWidth: 640 }}>
              Aviso, instruções de compra, datas e cupons da página inicial do link das famílias de cada escola, e notas na página de cada série.
            </p>

            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 20 }}>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Buscar escola…" aria-label="Buscar escola"
                style={{ ...adminInput, flex: "1 1 260px", maxWidth: 420 }}/>
              <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13.5, color: "var(--text-body)", cursor: "pointer" }}>
                <input type="checkbox" checked={onlyFilled} onChange={e => setOnlyFilled(e.target.checked)} style={{ accentColor: "var(--teal-700)" }}/>
                Só com notas{schools ? ` (${filledCount})` : ""}
              </label>
            </div>

            {error && (
              <div role="alert" style={{ marginTop: 18, padding: 14, borderRadius: 12, background: "#fdecec", color: "#6b1d1d", fontSize: 14 }}>
                {error} <button type="button" onClick={load} style={{ marginLeft: 8, background: "none", border: "none", color: "#6b1d1d", fontWeight: 700, textDecoration: "underline", cursor: "pointer" }}>Tentar de novo</button>
              </div>
            )}
            {!schools && !error && <div style={{ marginTop: 24, fontSize: 14, color: "var(--text-muted)" }}>Carregando escolas…</div>}

            {schools && (
              <div style={{ marginTop: 16, display: "grid", gap: 8 }}>
                {shown.length === 0 && <div style={{ fontSize: 14, color: "var(--text-muted)", padding: 16 }}>Nenhuma escola encontrada.</div>}
                {shown.map(s => {
                  const blocks = adminNoticeBlocks(s.familyNotice);
                  const seriesCount = adminSeriesCount(s);
                  const badge = [
                    blocks && `${blocks} ${blocks === 1 ? "bloco" : "blocos"}`,
                    seriesCount && `${seriesCount} ${seriesCount === 1 ? "série" : "séries"}`,
                  ].filter(Boolean).join(" · ");
                  return (
                    <button key={s.id} type="button" onClick={() => setSelectedId(s.id)} style={{
                      display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left",
                      padding: "13px 16px", borderRadius: 12, background: "#fff", cursor: "pointer",
                      border: "1px solid var(--border-card)", boxShadow: "var(--shadow-card)",
                    }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 15, fontWeight: 600, color: "var(--text-strong)" }}>{s.name}</span>
                        <span style={{ display: "block", fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
                          Ano letivo {s.season || "—"}{s.familyNoticeUpdatedAt ? ` · editado em ${adminWhen(s.familyNoticeUpdatedAt)}` : ""}
                        </span>
                      </span>
                      <span style={{
                        flexShrink: 0, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                        background: badge ? "var(--green-050)" : "var(--paper-200)", color: badge ? "var(--green-700)" : "var(--slate-600)",
                      }}>{badge || "Sem notas"}</span>
                      <span aria-hidden="true" style={{ color: "var(--teal-200)", fontSize: 18 }}>›</span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Tela restrita: conta logada sem adm-system.
function AdminForbidden() {
  return (
    <div className="fam-ds" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "var(--paper-100)" }}>
      <style>{FAM_DS_CSS}</style>
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text-strong)" }}>Área restrita</div>
        <p style={{ fontSize: 14, color: "var(--text-muted)", lineHeight: 1.55 }}>A área admin é só para o time Scolados. Se você faz parte do time, peça para darem o papel de administrador à sua conta.</p>
        <a href="#" style={{ fontSize: 14, fontWeight: 600, color: "var(--teal-700)" }}>← Voltar ao assistente</a>
      </div>
    </div>
  );
}

// #admin: null quando o hash não é da área admin (o App segue o fluxo
// normal); sem conta logada também null — cai no login e, depois de entrar,
// o hash continua #admin.
function renderAdminRoute(hash, account) {
  if (hash !== "admin" && !hash.startsWith("admin/")) return null;
  if (!account) return null;
  const isAdmin = account.user && account.user.role === "adm-system";
  return isAdmin ? <AdminArea account={account}/> : <AdminForbidden/>;
}

Object.assign(window, { renderFamiliaRoute, renderAdminRoute, exportCoordinatorCSV, exportListaPorTurma });
